import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Search, Plus, X, MapPin, ShieldCheck, AlertTriangle,
  Pause, Trash2, ArrowLeft, Mail, Phone, Globe, RefreshCw,
  Download, ExternalLink, CheckCircle2, Link2, Copy, Check,
  DollarSign, Bell, BarChart3, Target, ListChecks, Workflow, Megaphone, Sticker,
  Trophy, Sliders, Clock, Sparkles, Truck, Package, Send, KeyRound,
  Upload, Image as ImgIcon, AtSign, Wallet, Info,
  ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { PortalShell } from "@/components/PortalShell";
import { TradePortalInvitePanel } from "@/components/admin/TradePortalInvitePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PWSStatusPanel } from "@/components/admin/PWSStatusPanel";
import PartnerBusinessDetailsPanel from "@/components/admin/PartnerBusinessDetailsPanel";

const AU_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

const PWS_STATUS_PILL = {
  draft:     { bg: "bg-amber-100",   text: "text-amber-800",   label: "Draft" },
  active:    { bg: "bg-emerald-100", text: "text-emerald-800", label: "Active" },
  inactive:  { bg: "bg-slate-100",   text: "text-slate-600",   label: "Inactive" },
  suspended: { bg: "bg-rose-100",    text: "text-rose-800",    label: "Suspended" },
};

function StatusChip({ status }) {
  const p = PWS_STATUS_PILL[status] || PWS_STATUS_PILL.draft;
  return (
    <span
      data-testid={`pws-status-chip-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${p.bg} ${p.text}`}
    >
      {p.label}
    </span>
  );
}

// Feb 2026 — Trade Portal login state chip. Sits next to the
// business name in the list row and on the drawer header so an admin
// can see at a glance which partners are stuck at
// `password_set_awaiting_approval` (the Reece @ Cooloola class of bug).
const LOGIN_STATE_PILL = {
  not_invited:                     { bg: "bg-slate-100",   text: "text-slate-600",   label: "Not invited" },
  invited_password_not_set:        { bg: "bg-sky-100",     text: "text-sky-800",     label: "Invited" },
  password_set_awaiting_approval:  { bg: "bg-amber-100",   text: "text-amber-800",   label: "Awaiting approval" },
  active:                          { bg: "bg-emerald-100", text: "text-emerald-800", label: "Active" },
  rejected:                        { bg: "bg-rose-100",    text: "text-rose-800",    label: "Rejected" },
  disabled:                        { bg: "bg-rose-100",    text: "text-rose-800",    label: "Disabled" },
};

function LoginStateChip({ state, compact = false }) {
  if (!state) return null;
  const p = LOGIN_STATE_PILL[state] || LOGIN_STATE_PILL.not_invited;
  const size = compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]";
  return (
    <span
      data-testid={`pws-login-state-chip-${state}`}
      className={`inline-flex items-center gap-1 ${size} rounded-full font-semibold ${p.bg} ${p.text}`}
      title={
        state === "password_set_awaiting_approval"
          ? "Partner set their password but the admin hasn't approved the login yet — click into the drawer to approve."
          : state === "invited_password_not_set"
          ? "Trade Portal invite email sent — partner hasn't set their password yet."
          : state === "not_invited"
          ? "No Trade Portal login exists — click Send Trade Portal invite in the drawer."
          : undefined
      }
    >
      {p.label}
    </span>
  );
}

function MetricTile({ label, value, accent = "cyan", testid }) {
  const colour = {
    cyan: "text-cyan-700 bg-cyan-50",
    amber: "text-amber-700 bg-amber-50",
    emerald: "text-emerald-700 bg-emerald-50",
    slate: "text-slate-700 bg-slate-100",
    rose: "text-rose-700 bg-rose-50",
  }[accent];
  return (
    <div data-testid={testid} className="rounded-3xl bg-white border border-slate-200 p-5">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${colour}`}>
        <Users className="w-4 h-4" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

export default function AdminPWSPartnerNetwork() {
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pwsStatusFilter, setPwsStatusFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [needsReview, setNeedsReview] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);

  // Feb 2026 — deep-link support: `/admin/partners/network?open=<id>` opens
  // the drawer for that partner on mount. The Rollout Overview chips
  // and the "Fix Missing Pieces" button both use this pattern (plus a
  // hash such as `#rollout-branding` that the drawer's RolloutSetupPanel
  // reads to auto-expand the right accordion).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const openId = params.get("open");
    if (openId) setSelectedId(openId);
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (pwsStatusFilter) params.pws_status = pwsStatusFilter;
    if (stateFilter) params.state = stateFilter;
    if (sourceFilter) params.import_source = sourceFilter;
    if (needsReview) params.needs_review = "true";
    if (searchQ) params.q = searchQ;
    Promise.all([
      api.get("/admin/pws-partners", { params }).then(({ data }) => setItems(data.items || [])),
      api.get("/admin/pws-partners/metrics").then(({ data }) => setMetrics(data)),
    ])
      .catch(() => toast.error("Failed to load PWS partners"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [pwsStatusFilter, stateFilter, sourceFilter, needsReview]);

  return (
    <PortalShell>
      <div data-testid="admin-pws-partner-network-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/admin/partners"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
          data-testid="back-to-partners-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Partners
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">
              Aware Partner Network · Phase 1B
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              PWS Franchisee Network
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-2xl">
              Pristine Water Systems franchisees who white-label Aware testing in their region.
              New rows always start as <strong>draft</strong> — admin must manually review and
              activate before a partner is eligible to receive bookings.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/admin/partners/commissions"
              data-testid="pws-commissions-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50"
            >
              <DollarSign className="w-4 h-4" /> Commission Ledger
            </Link>
            <Link
              to="/admin/partners/notifications"
              data-testid="pws-notifications-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-amber-200 text-amber-800 hover:bg-amber-50"
            >
              <Bell className="w-4 h-4" /> Notification Log
            </Link>
            <Link
              to="/admin/partners/reporting"
              data-testid="pws-reporting-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-cyan-200 text-cyan-800 hover:bg-cyan-50"
            >
              <BarChart3 className="w-4 h-4" /> Reporting
            </Link>
            <Link
              to="/admin/partners/lead-value"
              data-testid="pws-lead-value-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50"
            >
              <Target className="w-4 h-4" /> Lead Value
            </Link>
            <Link
              to="/admin/partners/follow-ups"
              data-testid="pws-follow-ups-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-violet-200 text-violet-800 hover:bg-violet-50"
            >
              <ListChecks className="w-4 h-4" /> Follow-Ups
            </Link>
            <Link
              to="/admin/partners/opportunities"
              data-testid="pws-opportunity-workflow-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-amber-200 text-amber-800 hover:bg-amber-50"
            >
              <Workflow className="w-4 h-4" /> Opportunity Workflow
            </Link>
            <Link
              to="/admin/partners/customer-follow-ups"
              data-testid="pws-customer-follow-ups-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-blue-200 text-blue-800 hover:bg-blue-50"
            >
              <Mail className="w-4 h-4" /> Customer Emails
            </Link>
            <Link
              to="/admin/partners/customers"
              data-testid="pws-partner-customers-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50"
            >
              <Users className="w-4 h-4" /> Customers
            </Link>
            <Link
              to="/admin/partners/marketing-kit"
              data-testid="pws-marketing-kit-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-sky-200 text-sky-800 hover:bg-sky-50"
            >
              <Megaphone className="w-4 h-4" /> Marketing Kit
            </Link>
            <Link
              to="/admin/partners/field-labels"
              data-testid="pws-field-labels-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-cyan-200 text-cyan-800 hover:bg-cyan-50"
            >
              <Sticker className="w-4 h-4" /> Field Labels
            </Link>
            <Link
              to="/admin/partners/rankings"
              data-testid="pws-partner-rankings-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-amber-200 text-amber-800 hover:bg-amber-50"
            >
              <Trophy className="w-4 h-4" /> Rankings
            </Link>
            <Link
              to="/admin/partners/activity-controls"
              data-testid="pws-partner-activity-controls-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-violet-200 text-violet-800 hover:bg-violet-50"
            >
              <Sliders className="w-4 h-4" /> Activity Controls
            </Link>
            <Link
              to="/admin/partners/service-levels"
              data-testid="pws-partner-service-levels-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-teal-200 text-teal-800 hover:bg-teal-50"
            >
              <Clock className="w-4 h-4" /> Service Levels
            </Link>
            <Link
              to="/admin/partners/success-pack"
              data-testid="pws-success-pack-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-fuchsia-200 text-fuchsia-800 hover:bg-fuchsia-50"
            >
              <Sparkles className="w-4 h-4" /> Success Pack
            </Link>
            <Link
              to="/admin/partners/sample-collection"
              data-testid="pws-sample-collection-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-lime-200 text-lime-800 hover:bg-lime-50"
            >
              <Truck className="w-4 h-4" /> Sample Collection
            </Link>
            <Link
              to="/admin/partners/payouts"
              data-testid="pws-payout-runs-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50"
            >
              <Wallet className="w-4 h-4" /> Payouts
            </Link>
            <Link
              to="/admin/partners/wholesale-bundles"
              data-testid="pws-wholesale-bundles-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-violet-200 text-violet-800 hover:bg-violet-50"
            >
              <Package className="w-4 h-4" /> Wholesale Bundles
            </Link>
            <Button
              data-testid="pws-import-btn"
              onClick={() => setShowImport(true)}
              variant="outline"
              className="rounded-full font-semibold border-amber-300 text-amber-800 hover:bg-amber-50"
            >
              <Download className="w-4 h-4 mr-1.5" /> Import from PWS directory
            </Button>
            <Button
              data-testid="pws-partner-create-btn"
              onClick={() => setShowCreate(true)}
              className="btn-aqua rounded-full font-semibold"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add franchisee (draft)
            </Button>
          </div>
        </div>

        {/* Safety banner — pinned by tests to make the no-email guarantee visible */}
        <div
          data-testid="pws-safety-banner"
          className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/60 px-5 py-3.5 flex items-start gap-3"
        >
          <ShieldCheck className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <strong className="text-amber-950">Safe-mode (Phase 1A + 1B):</strong>{" "}
            Creating, editing, or importing a PWS franchisee row does <strong>NOT</strong>{" "}
            send any email, issue a password-reset token, send a login invite, create a
            login account, enable white-label reporting, enable public lead allocation,
            or enable commission. Each imported row is <strong>draft</strong>, with{" "}
            <strong>admin_review_required=true</strong> until you complete activation.
          </div>
        </div>

        {/* Metrics chips */}
        {metrics && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-4" data-testid="pws-metrics">
            <MetricTile
              testid="pws-metric-total"
              label="Total franchisees"
              value={metrics.total}
              accent="cyan"
            />
            <MetricTile
              testid="pws-metric-draft"
              label="Draft (awaiting review)"
              value={metrics.by_pws_status?.draft || 0}
              accent="amber"
            />
            <MetricTile
              testid="pws-metric-needs-review"
              label="Needs review"
              value={metrics.needs_review_count || 0}
              accent="rose"
            />
            <MetricTile
              testid="pws-metric-active"
              label="Active"
              value={metrics.by_pws_status?.active || 0}
              accent="emerald"
            />
            <MetricTile
              testid="pws-metric-paused"
              label="Inactive / Suspended"
              value={(metrics.by_pws_status?.inactive || 0) + (metrics.by_pws_status?.suspended || 0)}
              accent="slate"
            />
          </div>
        )}

        {/* Filters */}
        <div className="mt-7 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="pws-search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search by business, contact, region, postcode…"
              className="pl-10"
            />
          </div>
          <Select value={pwsStatusFilter || "all"} onValueChange={(v) => setPwsStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="pws-status-filter" className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(PWS_STATUS_PILL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={stateFilter || "all"} onValueChange={(v) => setStateFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="pws-state-filter" className="w-32">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              {AU_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sourceFilter || "all"} onValueChange={(v) => setSourceFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="pws-source-filter" className="w-44">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="manual_admin">Manual</SelectItem>
              <SelectItem value="pws_directory_import">PWS directory</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid="pws-needs-review-filter"
            variant={needsReview ? "default" : "outline"}
            onClick={() => setNeedsReview((v) => !v)}
            className={needsReview ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
          >
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
            Needs review only
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={load}
            data-testid="pws-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Table */}
        <div className="mt-4 bg-white rounded-3xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3">Franchisee</th>
                  <th className="text-left px-5 py-3">Region</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Service area</th>
                  <th className="text-left px-5 py-3">Commission</th>
                  <th className="text-left px-5 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-12 text-slate-500" data-testid="pws-loading">
                      Loading…
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-12 text-slate-500" data-testid="pws-empty-state">
                      <Users className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                      No franchisees yet. Click <strong>Add franchisee</strong> to create a manual draft row,
                      or click <strong>Import from PWS directory</strong> to bulk-import the public franchise regions.
                    </td>
                  </tr>
                ) : (
                  items.map((p) => (
                    <tr
                      key={p.id}
                      data-testid={`pws-partner-row-${p.id}`}
                      onClick={() => setSelectedId(p.id)}
                      className="border-t border-slate-100 hover:bg-slate-50/50 cursor-pointer"
                    >
                      <td className="px-5 py-3.5">
                        <div className="font-medium text-slate-900 flex items-center gap-2">
                          <span>{p.business_name || "—"}</span>
                          {p.import_source === "pws_directory_import" && (
                            <span
                              data-testid={`pws-row-imported-chip-${p.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-800 uppercase tracking-wider"
                            >
                              Imported
                            </span>
                          )}
                          {p.admin_review_required && p.pws_partner_status !== "active" && (
                            <span
                              data-testid={`pws-row-needs-review-chip-${p.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-rose-100 text-rose-700 uppercase tracking-wider"
                            >
                              Review
                            </span>
                          )}
                          <LoginStateChip state={p.user_state} compact />
                        </div>
                        <div className="text-xs text-slate-500">{p.email}</div>
                      </td>
                      <td className="px-5 py-3.5 text-slate-700">
                        <div className="font-medium">{p.franchise_region}</div>
                        <div className="text-xs text-slate-500">{p.state}</div>
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusChip status={p.pws_partner_status || "draft"} />
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-600">
                        {(p.service_postcodes || []).length} postcodes
                        {p.service_suburbs?.length ? ` · ${p.service_suburbs.length} suburbs` : ""}
                      </td>
                      <td className="px-5 py-3.5 text-slate-700">{Number(p.commission_pct ?? 0).toFixed(1)}%</td>
                      <td className="px-5 py-3.5 text-xs text-slate-500">{formatDateTimeAU(p.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedId && (
        <PWSPartnerDrawer
          partnerId={selectedId}
          onClose={() => { setSelectedId(null); load(); }}
        />
      )}
      {showCreate && (
        <PWSPartnerCreateModal
          onClose={(created) => {
            setShowCreate(false);
            if (created) load();
          }}
        />
      )}
      {showImport && (
        <PWSDirectoryImportModal
          onClose={(applied) => {
            setShowImport(false);
            if (applied) load();
          }}
        />
      )}
    </PortalShell>
  );
}

// ─── Create Modal ────────────────────────────────────────────────────────
function PWSPartnerCreateModal({ onClose }) {
  const [form, setForm] = useState({
    email: "",
    business_name: "",
    contact_name: "",
    phone: "",
    website: "",
    franchise_region: "",
    abn: "",
    state: "QLD",
    office_address: "",
    service_postcodes: "",
    service_suburbs: "",
    mail_out_enabled: true,
    commission_pct: 15,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (v) => setForm((f) => ({ ...f, [k]: v === "true" }));

  const submit = async () => {
    if (!form.email || !form.business_name || !form.franchise_region) {
      toast.error("Business name, email and franchise region are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        commission_pct: Number(form.commission_pct) || 0,
        service_postcodes: form.service_postcodes
          .split(/[,\s]+/).map((s) => s.trim()).filter(Boolean),
        service_suburbs: form.service_suburbs
          .split(/[,;]/).map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/admin/pws-partners", payload);
      toast.success("Draft franchisee created — no email was sent.");
      onClose(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create franchisee");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="pws-create-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full p-7 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Add PWS franchisee (draft)</h2>
            <p className="text-xs text-slate-500 mt-1">
              The row will be saved with status <strong>draft</strong>.
              No email, login, or invitation will be sent.
            </p>
          </div>
          <button onClick={() => onClose(false)} className="p-1.5 hover:bg-slate-100 rounded-lg" aria-label="Close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Business name *</Label>
            <Input data-testid="pws-create-business-name" value={form.business_name} onChange={set("business_name")} />
          </div>
          <div>
            <Label>Contact email *</Label>
            <Input data-testid="pws-create-email" type="email" value={form.email} onChange={set("email")} />
          </div>
          <div>
            <Label>Contact name</Label>
            <Input data-testid="pws-create-contact-name" value={form.contact_name} onChange={set("contact_name")} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="pws-create-phone" value={form.phone} onChange={set("phone")} />
          </div>
          <div>
            <Label>Website</Label>
            <Input data-testid="pws-create-website" value={form.website} onChange={set("website")} />
          </div>

          <div>
            <Label>Franchise region *</Label>
            <Input
              data-testid="pws-create-region"
              placeholder="e.g. Noosa, Cairns"
              value={form.franchise_region}
              onChange={set("franchise_region")}
            />
          </div>
          <div>
            <Label>State *</Label>
            <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
              <SelectTrigger data-testid="pws-create-state"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AU_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>ABN</Label>
            <Input data-testid="pws-create-abn" value={form.abn} onChange={set("abn")} />
          </div>
          <div className="sm:col-span-2">
            <Label>Office address</Label>
            <Input data-testid="pws-create-office-address" value={form.office_address} onChange={set("office_address")} />
          </div>
          <div className="sm:col-span-2">
            <Label>Service postcodes (comma or space separated)</Label>
            <Input
              data-testid="pws-create-postcodes"
              placeholder="4566 4567 4568"
              value={form.service_postcodes}
              onChange={set("service_postcodes")}
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Service suburbs (comma separated)</Label>
            <Input
              data-testid="pws-create-suburbs"
              placeholder="Noosaville, Tewantin, Cooroy"
              value={form.service_suburbs}
              onChange={set("service_suburbs")}
            />
          </div>

          <div>
            <Label>Commission %</Label>
            <Input
              data-testid="pws-create-commission"
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={form.commission_pct}
              onChange={set("commission_pct")}
            />
          </div>
          <div>
            <Label>Mail-out enabled?</Label>
            <Select
              value={form.mail_out_enabled ? "true" : "false"}
              onValueChange={setBool("mail_out_enabled")}
            >
              <SelectTrigger data-testid="pws-create-mailout"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Yes — mail kits by default</SelectItem>
                <SelectItem value="false">No — local pickup only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2">
            <Label>Internal notes</Label>
            <Textarea
              data-testid="pws-create-notes"
              rows={3}
              value={form.notes}
              onChange={set("notes")}
              placeholder="Anything the admin team should know about this franchisee"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 border-t border-slate-100 pt-5">
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button
            data-testid="pws-create-submit"
            disabled={saving}
            onClick={submit}
            className="btn-aqua font-semibold"
          >
            {saving ? "Saving…" : "Create draft franchisee"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── PWS Directory Import Modal ──────────────────────────────────────────
function PWSDirectoryImportModal({ onClose }) {
  const [step, setStep] = useState("idle"); // idle | preview | applied
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);

  const runDryRun = async () => {
    setLoading(true);
    setStep("idle");
    try {
      const { data } = await api.post("/admin/pws-partners/import/dry-run", {});
      setPreview(data);
      setStep("preview");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Dry-run failed");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!preview) return;
    setLoading(true);
    try {
      const { data } = await api.post(
        "/admin/pws-partners/import/apply", { confirm: true },
      );
      setApplied(data);
      setStep("applied");
      toast.success(`Imported ${data.created_count} draft franchisees — no emails were sent.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Apply failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
      data-testid="pws-import-modal"
    >
      <div className="bg-white rounded-3xl max-w-3xl w-full p-7 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700">
              Phase 1B · PWS Directory Import
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">
              Import franchise regions from{" "}
              <a
                href="https://pristinewatersystems.com.au/available-franchise-regions/"
                target="_blank"
                rel="noreferrer"
                className="text-amber-700 hover:text-amber-800 underline decoration-amber-300"
                data-testid="pws-import-source-link"
              >
                pristinewatersystems.com.au
              </a>
            </h2>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Reads the public franchise-regions page and creates one DRAFT row per region.
              No emails are sent. No password-reset tokens are issued. No logins are created.
              Every imported row has commission, white-label, and public-lead allocation
              <strong> disabled</strong> until the admin explicitly turns each on.
            </p>
          </div>
          <button
            onClick={() => onClose(step === "applied")}
            className="p-1.5 hover:bg-slate-100 rounded-lg"
            aria-label="Close"
            data-testid="pws-import-close-btn"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Safety guarantees panel */}
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 mb-5" data-testid="pws-import-safety-list">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-amber-700" />
            <div className="text-xs font-bold text-amber-900 uppercase tracking-wider">
              Safety guarantees
            </div>
          </div>
          <ul className="text-xs text-amber-900 space-y-1 ml-1">
            <li>Every imported row is <strong>draft</strong> with <code>admin_review_required = true</code></li>
            <li><strong>No</strong> emails will be sent to any franchisee</li>
            <li><strong>No</strong> password-reset tokens will be issued</li>
            <li><strong>No</strong> partner login accounts will be created</li>
            <li><strong>No</strong> login invites will be sent</li>
            <li>White-label reporting <strong>disabled</strong> on every imported row</li>
            <li>Public lead allocation <strong>disabled</strong> on every imported row</li>
            <li>Commission <strong>disabled</strong> with commission_pct = 0 on every imported row</li>
            <li>Duplicates against existing partners are detected and <strong>skipped</strong></li>
          </ul>
        </div>

        {step === "idle" && (
          <div className="text-center py-8">
            <Download className="w-10 h-10 mx-auto text-amber-600 mb-3" />
            <p className="text-sm text-slate-700 max-w-md mx-auto">
              Click below to fetch the public franchise-regions page and show a dry-run preview.
              Nothing will be written to the database until you confirm.
            </p>
            <Button
              data-testid="pws-import-dry-run-btn"
              disabled={loading}
              onClick={runDryRun}
              className="mt-5 btn-aqua font-semibold"
            >
              {loading ? "Fetching…" : "Preview import (dry run)"}
            </Button>
          </div>
        )}

        {step === "preview" && preview && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div data-testid="pws-import-preview-scanned" className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Scanned</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{preview.rows_scanned}</div>
              </div>
              <div data-testid="pws-import-preview-create" className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Would create</div>
                <div className="font-display text-2xl font-bold text-emerald-800 mt-1">{preview.would_create}</div>
              </div>
              <div data-testid="pws-import-preview-skip" className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Would skip (dupes)</div>
                <div className="font-display text-2xl font-bold text-amber-800 mt-1">{preview.would_skip}</div>
              </div>
            </div>
            <div className="text-xs text-slate-500 mb-3 flex items-center gap-2 flex-wrap">
              <ExternalLink className="w-3 h-3" />
              <span>Source: <a className="underline text-cyan-700 hover:text-cyan-800" href={preview.source_url} target="_blank" rel="noreferrer">{preview.source_url}</a></span>
              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-[10px]">
                {preview.source === "live" ? "LIVE" : `SNAPSHOT ${preview.snapshot_date}`}
              </span>
            </div>

            <div className="border border-slate-200 rounded-2xl overflow-hidden mb-4" data-testid="pws-import-preview-table">
              <div className="overflow-x-auto max-h-[280px]">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2">Action</th>
                      <th className="text-left px-4 py-2">State</th>
                      <th className="text-left px-4 py-2">Region</th>
                      <th className="text-left px-4 py-2">Suburbs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.creates.map((c, i) => (
                      <tr key={`c-${i}`} className="border-t border-slate-100" data-testid={`pws-import-create-row-${i}`}>
                        <td className="px-4 py-2"><span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">CREATE</span></td>
                        <td className="px-4 py-2 text-slate-700 font-medium">{c.state}</td>
                        <td className="px-4 py-2 text-slate-900">{c.region}</td>
                        <td className="px-4 py-2 text-slate-500">{(c.suburbs || []).join(", ")}</td>
                      </tr>
                    ))}
                    {preview.skips.map((s, i) => (
                      <tr key={`s-${i}`} className="border-t border-slate-100 bg-amber-50/30" data-testid={`pws-import-skip-row-${i}`}>
                        <td className="px-4 py-2"><span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">SKIP</span></td>
                        <td className="px-4 py-2 text-slate-700 font-medium">{s.state}</td>
                        <td className="px-4 py-2 text-slate-900">{s.region}</td>
                        <td className="px-4 py-2 text-slate-500">{s.reason} → {s.duplicate_match?.business_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => setStep("idle")} disabled={loading}>
                Cancel
              </Button>
              <Button
                data-testid="pws-import-apply-btn"
                disabled={loading || preview.would_create === 0}
                onClick={apply}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              >
                {loading ? "Importing…" : `Apply \u2014 import ${preview.would_create} drafts (no emails)`}
              </Button>
            </div>
          </>
        )}

        {step === "applied" && applied && (
          <div className="text-center py-6" data-testid="pws-import-applied">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-3" />
            <h3 className="font-display text-xl font-bold text-slate-900">
              Imported {applied.created_count} draft franchisees
            </h3>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
              {applied.safety_summary}
            </p>
            <div className="grid grid-cols-3 gap-3 mt-5 max-w-md mx-auto">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Created</div>
                <div className="font-display text-2xl font-bold text-emerald-800 mt-1" data-testid="pws-import-applied-created">{applied.created_count}</div>
              </div>
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Skipped</div>
                <div className="font-display text-2xl font-bold text-amber-800 mt-1" data-testid="pws-import-applied-skipped">{applied.skipped_count}</div>
              </div>
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Source</div>
                <div className="font-display text-xs font-mono font-bold text-slate-700 mt-1.5">
                  {applied.source === "live" ? "LIVE" : `SNAP ${applied.snapshot_date}`}
                </div>
              </div>
            </div>
            <Button
              data-testid="pws-import-done-btn"
              onClick={() => onClose(true)}
              className="mt-6 btn-aqua font-semibold"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Drawer (detail + edit + transitions) ───────────────────────────────
function PWSPartnerDrawer({ partnerId, onClose }) {
  const [partner, setPartner] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchOne = () =>
    api.get(`/admin/pws-partners/${partnerId}`).then(({ data }) => {
      setPartner(data);
      setEdit(null);
    });

  useEffect(() => { fetchOne(); }, [partnerId]);

  // Phase 2 — refetch when the booking-link panel saves a new slug
  useEffect(() => {
    const refetch = () => fetchOne();
    window.addEventListener("pws-partner-updated", refetch);
    return () => window.removeEventListener("pws-partner-updated", refetch);
  }, [partnerId]);

  const startEdit = () => {
    setEdit({
      business_name: partner.business_name || "",
      contact_name: partner.contact_name || "",
      email: partner.email || "",
      phone: partner.phone || "",
      website: partner.website || "",
      franchise_region: partner.franchise_region || "",
      abn: partner.abn || "",
      state: partner.state || "QLD",
      office_address: partner.office_address || "",
      service_postcodes: (partner.service_postcodes || []).join(" "),
      service_suburbs: (partner.service_suburbs || []).join(", "),
      mail_out_enabled: partner.mail_out_enabled !== false,
      commission_pct: partner.commission_pct ?? 15,
      notes: partner.notes || "",
    });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      // Feb 2026 (production fix): sanitize the payload before PATCH.
      // The previous version blindly spread `...edit`, so an
      // accidentally-empty field (e.g. email cleared while another
      // field was being fixed) tripped a 422 and the toast was
      // easily missed. Now: trim strings, drop empty-string keys for
      // OPTIONAL fields, keep numbers / booleans / arrays as-is.
      const optionalStringFields = new Set([
        "business_name", "contact_name", "email", "phone", "website",
        "franchise_region", "abn", "state", "office_address", "notes",
      ]);
      const cleaned = {};
      for (const [k, v] of Object.entries(edit)) {
        if (v === null || v === undefined) continue;
        if (typeof v === "string") {
          const trimmed = v.trim();
          if (optionalStringFields.has(k) && trimmed === "") continue;
          cleaned[k] = trimmed;
        } else {
          cleaned[k] = v;
        }
      }
      const payload = {
        ...cleaned,
        commission_pct: Number(edit.commission_pct) || 0,
        service_postcodes: (edit.service_postcodes || "")
          .split(/[,\s]+/).map((s) => s.trim()).filter(Boolean),
        service_suburbs: (edit.service_suburbs || "")
          .split(/[,;]/).map((s) => s.trim()).filter(Boolean),
        // These two are turned into arrays above, so remove the raw
        // trimmed strings that leaked in via `cleaned`.
      };
      delete payload.service_postcodes_str;
      delete payload.service_suburbs_str;
      await api.patch(`/admin/pws-partners/${partnerId}`, payload);
      toast.success("Saved — no email was sent.");
      await fetchOne();
    } catch (err) {
      // Surface field-scoped 422 errors so admins see exactly which
      // field is invalid instead of a silent "Failed to save".
      const resp = err?.response;
      const detail = resp?.data?.detail;
      if (resp?.status === 422 && Array.isArray(detail)) {
        const fields = detail
          .map((d) => {
            const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "field";
            return `${loc}: ${d?.msg || "invalid"}`;
          })
          .join(" · ");
        toast.error(`Save failed — ${fields}`);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail && typeof detail === "object") {
        toast.error(detail.message || JSON.stringify(detail));
      } else {
        toast.error("Failed to save");
      }
    } finally {
      setBusy(false);
    }
  };

  const transition = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/pws-partners/${partnerId}/${action}`, { reason: "" });
      toast.success(`Status updated (${action}) — no email was sent.`);
      await fetchOne();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "missing_required_fields") {
        toast.error(`Missing fields: ${detail.fields.join(", ")}`);
      } else {
        toast.error(typeof detail === "string" ? detail : `Failed to ${action}`);
      }
    } finally {
      setBusy(false);
    }
  };

  // Feb 2026 — manual "Mark as reviewed" flow. `admin_review_required`
  // is auto-cleared on activation, but admins may want to dismiss the
  // review flag for imported rows they don't want to activate yet
  // (e.g. sales prospects that are still verifying their info).
  const markReviewed = async () => {
    if (!window.confirm("Clear the 'Needs review' flag for this partner? You can always re-flag them later via the edit form.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/pws-partners/${partnerId}/mark-reviewed`);
      toast.success("Review flag cleared.");
      await fetchOne();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to clear review flag");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this draft franchisee row? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/pws-partners/${partnerId}`);
      toast.success("Draft franchisee deleted");
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to delete");
      setBusy(false);
    }
  };

  if (!partner) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center" data-testid="pws-drawer-loading">
        <div className="bg-white rounded-2xl px-6 py-4 text-sm text-slate-600">Loading…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end" data-testid="pws-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto p-7">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-600">
              PWS Franchisee · {partner.franchise_region} ({partner.state})
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1" data-testid="pws-drawer-business-name">
              {partner.business_name}
            </h2>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <StatusChip status={partner.pws_partner_status || "draft"} />
              {(partner.trade_portal_user_id == null && partner.partner_user_id == null) && (
                <span
                  data-testid="pws-no-login-chip"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600"
                >
                  No login provisioned
                </span>
              )}
              {partner.admin_review_required && (
                <button
                  type="button"
                  onClick={markReviewed}
                  disabled={busy}
                  data-testid="pws-needs-review-chip"
                  title="Click to clear the review flag"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors disabled:opacity-60"
                >
                  Needs review <span className="text-[9px] opacity-70">· dismiss</span>
                </button>
              )}
              {partner.import_source === "pws_directory_import" && (
                <span
                  data-testid="pws-imported-chip"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800"
                >
                  Imported from PWS directory
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg" aria-label="Close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {!edit ? (
          <>
            <div className="space-y-3 text-sm">
              <Row icon={Mail} label="Email" value={partner.email} />
              {partner.contact_name && <Row icon={Users} label="Contact" value={partner.contact_name} />}
              {partner.phone && <Row icon={Phone} label="Phone" value={partner.phone} />}
              {partner.website && <Row icon={Globe} label="Website" value={partner.website} />}
              <Row icon={MapPin} label="Office" value={partner.office_address || "—"} />
              <Row icon={MapPin} label="Postcodes" value={(partner.service_postcodes || []).join(" · ") || "—"} />
              <Row icon={MapPin} label="Suburbs" value={(partner.service_suburbs || []).join(", ") || "—"} />
              <Row icon={ShieldCheck} label="Mail-out" value={partner.mail_out_enabled ? "Enabled" : "Disabled"} />
              <Row icon={ShieldCheck} label="Commission" value={`${Number(partner.commission_pct ?? 0).toFixed(1)}%`} />
              <Row icon={ShieldCheck} label="ABN" value={partner.abn || "—"} />
              <Row icon={ShieldCheck} label="Source" value={partner.import_source || "—"} />
              {partner.source_url && (
                <Row icon={ExternalLink} label="Public source" value={partner.source_url} />
              )}
              {partner.imported_at && (
                <Row icon={ExternalLink} label="Imported at" value={formatDateTimeAU(partner.imported_at)} />
              )}
            </div>

            {/* Phase 2 — partner booking-link panel. Active partners get a
                live link that customers can use; non-active partners see a
                preview that won't resolve until they're activated. */}
            <PartnerBookingLinkPanel partner={partner} />

            {/* Phase 24 — admin-only PWS status panel for this partner. */}
            <PWSStatusPanel
              kind="partner"
              identifier={partner.id}
              isAdmin={true}
            />

            {/* Phase 1B — capability flags panel. All OFF by default. */}
            <div
              data-testid="pws-capability-panel"
              className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/50 p-4"
            >
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
                Capability flags
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <CapabilityRow
                  label="White-label enabled"
                  value={partner.white_label_enabled}
                  hint="Applies to the customer Summary Report only. The COA always stays SCWT / Aware-branded regardless of this setting."
                />
                <CapabilityRow label="Public lead allocation" value={partner.receive_public_leads} />
                <CapabilityRow label="Commission enabled" value={partner.commission_enabled} />
                <CapabilityRow label="Regional commission" value={partner.regional_commission_enabled} />
                <CapabilityRow label="Login invite sent" value={partner.login_invite_sent} />
                <CapabilityRow label="Admin review required" value={partner.admin_review_required} muted />
              </div>
              {(partner.missing_fields || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700 mb-1">
                    Missing for activation
                  </div>
                  <div className="text-xs text-slate-700">
                    {(partner.missing_fields || []).join(" · ")}
                  </div>
                </div>
              )}
            </div>

            {/* Feb 2026 — compact branding / logo panel. Reuses the
                existing /admin/partners/{id}/branding endpoints so a
                logo uploaded here also appears in the property-manager
                drawer + on trade referral PDFs. Bug fix: this drawer
                previously showed no logo at all, so admins couldn't
                tell whether the default PWS logo (or a custom logo)
                had actually stuck. */}
            <PartnerLogoPanel partnerId={partner.id} />

            {/* Feb 2026 — admin-side Business Details form. Fills the
                same `business_details` sub-doc the partner-side
                onboarding page uses so the "Finish your Business
                Details" banner disappears without the partner having
                to retype the ABN / address themselves. Also carries
                a "Mark complete (override)" button for cases where
                the legal data is held off-platform. */}
            <PartnerBusinessDetailsPanel
              partnerId={partner.id}
              prefill={{
                business_name:  partner.business_name,
                abn:            partner.abn,
                office_address: partner.office_address,
                state:          partner.state,
              }}
            />

            {/* Feb 2026 — Trade Portal login invite panel. Provisions a
                users row + sends a 7-day password-setup email. Only
                rendered for portal-eligible partner types (pws_franchisee
                today). */}
            <TradePortalInvitePanel partnerId={partner.id} />

            {/* Feb 2026 — Login approval chip + Approve button. Sits
                right after the invite panel because it's the very
                next step in the invite → password → approval flow.
                Only surfaces the Approve button when the partner is
                actually stuck at `password_set_awaiting_approval`. */}
            <LoginApprovalPanel
              partnerId={partner.id}
              userState={partner.user_state}
              onApproved={fetchOne}
            />

            {/* Feb 2026 — Landing page provisioner. One-click Create
                for partners that don't have a public marketing page
                yet, or a status pill + link when one already exists. */}
            <LandingPagePanel
              partnerId={partner.id}
              landingPage={partner.landing_page}
              onCreated={fetchOne}
            />

            {/* Feb 2026 — Bulk Kit Inventory manual adjust panel.
                Only meaningful for PWS franchisees (the only partner
                type that participates in the Bulk Kit Ordering flow).
                Renders current available / ordered / used counts +
                a +/- adjust form with a mandatory reason. Every save
                writes an audit row surfaced in the history panel. */}
            {partner.partner_type === "pws_franchisee" && (
              <KitInventoryPanel
                partnerId={partner.id}
                partnerName={partner.business_name}
                onAdjusted={fetchOne}
              />
            )}

            {/* Feb 2026 — Rollout Setup panel. Closes the loop on the 4
                previously-read-only pillars (Booking, Branding, SLA,
                Sample Workflow) + surfaces Commission overrides + the
                Pickup/Drop-off toggle. Each subsection saves through
                its own dedicated backend endpoint so the Rollout
                Overview chips flip to green immediately. */}
            {partner.partner_type === "pws_franchisee" && (
              <RolloutSetupPanel
                partner={partner}
                onSaved={() => {
                  // Reload this drawer AND broadcast the change so
                  // the PWS Partner Overview page (rollout progress
                  // card + chips) refetches — even when it's the
                  // parent tab the admin came from. Without the
                  // event dispatch the overview stays visually
                  // stale until the admin manually hits "Refresh"
                  // or the tab is remounted.
                  fetchOne();
                  try {
                    window.dispatchEvent(new CustomEvent("pws-partner-updated", {
                      detail: { partner_id: partner.id },
                    }));
                  } catch (_) { /* no-op — CustomEvent unsupported on very old browsers */ }
                }}
              />
            )}

            {partner.notes && (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 mt-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Notes</div>
                <div className="text-slate-700 whitespace-pre-wrap text-sm">{partner.notes}</div>
              </div>
            )}

            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <Button data-testid="pws-drawer-edit-btn" onClick={startEdit} variant="outline">Edit details</Button>
              {(partner.pws_partner_status || "draft") === "draft" && (
                <Button
                  data-testid="pws-drawer-activate-btn"
                  disabled={busy}
                  onClick={() => transition("activate")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <ShieldCheck className="w-4 h-4 mr-1.5" /> Mark active
                </Button>
              )}
              {partner.pws_partner_status === "active" && (
                <Button
                  data-testid="pws-drawer-pause-btn"
                  disabled={busy}
                  onClick={() => transition("pause")}
                  variant="outline"
                >
                  <Pause className="w-4 h-4 mr-1.5" /> Pause
                </Button>
              )}
              {partner.pws_partner_status !== "suspended" && partner.pws_partner_status !== "draft" && (
                <Button
                  data-testid="pws-drawer-suspend-btn"
                  disabled={busy}
                  onClick={() => transition("suspend")}
                  variant="outline"
                  className="border-rose-200 text-rose-700 hover:bg-rose-50"
                >
                  <AlertTriangle className="w-4 h-4 mr-1.5" /> Suspend
                </Button>
              )}
              {(partner.pws_partner_status === "inactive" || partner.pws_partner_status === "suspended") && (
                <Button
                  data-testid="pws-drawer-restore-draft-btn"
                  disabled={busy}
                  onClick={() => transition("restore-draft")}
                  variant="outline"
                >
                  Restore to draft
                </Button>
              )}
              {(partner.pws_partner_status || "draft") === "draft" && (
                <Button
                  data-testid="pws-drawer-delete-btn"
                  disabled={busy}
                  onClick={remove}
                  variant="outline"
                  className="ml-auto border-rose-200 text-rose-700 hover:bg-rose-50"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete draft
                </Button>
              )}
            </div>

            {/* ─── Bank details (Feb 2026 P0 #1) ─── */}
            <AdminBankDetailsSection partnerId={partner.id} />

            {/* ─── Notification preferences (Feb 2026 P1) ─── */}
            <AdminNotificationPrefsSection partnerId={partner.id} />

            {partner.pws_status_history?.length > 0 && (
              <div className="mt-6 border-t border-slate-100 pt-5">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
                  Status history
                </div>
                <ol className="space-y-2 text-xs">
                  {partner.pws_status_history.slice().reverse().slice(0, 10).map((h, i) => (
                    <li key={i} className="text-slate-600">
                      <span className="font-semibold text-slate-900">{h.from} → {h.to}</span>
                      <span className="mx-1.5 text-slate-400">·</span>
                      <span>{h.by}</span>
                      <span className="mx-1.5 text-slate-400">·</span>
                      <span>{formatDateTimeAU(h.at)}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </>
        ) : (
          <EditForm
            form={edit}
            setForm={setEdit}
            onCancel={() => setEdit(null)}
            onSave={saveEdit}
            busy={busy}
            hasLinkedPortalUser={!!partner.trade_portal_user_id}
            currentLoginEmail={partner.email}
          />
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
        <div className="text-slate-800 break-words">{value}</div>
      </div>
    </div>
  );
}

function CapabilityRow({ label, value, muted, hint }) {
  const on = value === true;
  return (
    <div className="flex items-center justify-between rounded-lg bg-white border border-slate-100 px-3 py-2">
      <span className="text-slate-700 flex items-center gap-1.5">
        {label}
        {hint && (
          <span
            title={hint}
            data-testid={`pws-capability-hint-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="text-slate-400 hover:text-slate-600 cursor-help inline-flex"
            aria-label={hint}
          >
            <Info className="w-3 h-3" />
          </span>
        )}
      </span>
      <span
        data-testid={`pws-capability-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
          on
            ? muted
              ? "bg-amber-100 text-amber-800"
              : "bg-emerald-100 text-emerald-800"
            : "bg-slate-100 text-slate-500"
        }`}
      >
        {on ? "On" : "Off"}
      </span>
    </div>
  );
}

// ─── Feb 2026 — Compact partner logo / branding panel ───────────────
// The drawer used to render nothing about the partner logo, so admins
// couldn't tell whether the default PWS logo (or a custom uploaded
// logo) had actually stuck to the record. This panel is a compact
// mirror of PartnerBrandingSection — read, upload, replace, remove —
// wired to the same /admin/partners/{id}/branding endpoints.
const _LOGO_ACCEPT = "image/png,image/jpeg,image/webp";
const _LOGO_MAX_BYTES = 1024 * 1024;
const _LOGO_ALLOWED_MIMES = new Set(["image/png", "image/jpeg", "image/webp"]);
function PartnerLogoPanel({ partnerId }) {
  const [branding, setBranding] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reinstalling, setReinstalling] = useState(false);
  // Feb 2026 (production fix): distinguish between "no logo yet" and
  // "Mongo says has_logo=true but the file failed to load". The panel
  // only surfaces the Re-install action for the second case so the
  // default UX stays clean.
  const [logoBroken, setLogoBroken] = useState(false);
  const fileInputRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    setLogoBroken(false);
    try {
      const { data } = await api.get(`/admin/partners/${partnerId}/branding`);
      setBranding(data);
      if (logoUrl) URL.revokeObjectURL(logoUrl);
      if (data.has_logo) {
        try {
          const blob = await api.get(
            `/admin/partners/${partnerId}/branding/logo/file`,
            { responseType: "blob" },
          );
          // Guard against a 200-but-empty response — treat empty
          // blob as broken so the repair button surfaces.
          if (blob.data && blob.data.size > 0) {
            setLogoUrl(URL.createObjectURL(blob.data));
          } else {
            setLogoUrl(null);
            setLogoBroken(true);
          }
        } catch {
          // File endpoint 4xx/5xx — Mongo says has_logo but disk
          // returned no bytes. This is the exact production symptom.
          setLogoUrl(null);
          setLogoBroken(true);
        }
      } else {
        setLogoUrl(null);
      }
    } catch {
      setBranding(null);
      setLogoUrl(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (partnerId) refresh(); }, [partnerId]);
  useEffect(() => () => { if (logoUrl) URL.revokeObjectURL(logoUrl); }, [logoUrl]);

  const upload = async (file) => {
    if (!file) return;
    if (!_LOGO_ALLOWED_MIMES.has(file.type)) { toast.error("PNG, JPG, or WebP only"); return; }
    if (file.size > _LOGO_MAX_BYTES) { toast.error("Logo must be under 1 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/partners/${partnerId}/branding/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded");
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this partner's logo?")) return;
    try {
      await api.delete(`/admin/partners/${partnerId}/branding/logo`);
      toast.success("Logo removed");
      await refresh();
    } catch {
      toast.error("Remove failed");
    }
  };

  const reinstallDefault = async () => {
    setReinstalling(true);
    try {
      const { data } = await api.post(
        `/admin/pws-partners/${partnerId}/reinstall-default-logo`,
      );
      toast.success(
        data.action === "repaired_missing_disk_file"
          ? "Default logo re-installed (missing disk file repaired)."
          : "Default PWS logo installed."
      );
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" ? detail.message : detail) || "Reinstall failed";
      toast.error(msg);
    } finally {
      setReinstalling(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="pws-partner-logo-panel-loading"
        className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/50 p-4 text-xs text-slate-500"
      >
        Loading logo…
      </div>
    );
  }

  return (
    <div
      data-testid="pws-partner-logo-panel"
      className="mt-5 rounded-2xl border border-slate-200 bg-white p-4"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3 flex items-center gap-1.5">
        <ImgIcon className="w-3 h-3" /> Logo &amp; branding
      </div>
      <div className="flex gap-4 items-start">
        <div
          data-testid="pws-partner-logo-preview"
          className={`flex-shrink-0 w-28 h-20 rounded-xl border flex items-center justify-center overflow-hidden ${
            logoBroken
              ? "bg-rose-50 border-rose-200"
              : "bg-slate-50 border-slate-200"
          }`}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="partner logo"
              data-testid="pws-partner-logo-image"
              className="max-w-full max-h-full object-contain"
              onError={() => setLogoBroken(true)}
            />
          ) : logoBroken ? (
            <AlertTriangle
              data-testid="pws-partner-logo-broken-icon"
              className="w-6 h-6 text-rose-500"
            />
          ) : (
            <ImgIcon className="w-6 h-6 text-slate-300" />
          )}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <input
            ref={fileInputRef}
            data-testid="pws-partner-logo-file-input"
            type="file"
            accept={_LOGO_ACCEPT}
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              data-testid={branding?.has_logo ? "pws-partner-logo-replace-btn" : "pws-partner-logo-upload-btn"}
              className="rounded-full text-xs"
            >
              <Upload className="w-3 h-3 mr-1" />
              {uploading ? "Uploading…" : branding?.has_logo ? "Replace logo" : "Upload logo"}
            </Button>
            {branding?.has_logo && (
              <Button
                size="sm"
                variant="outline"
                onClick={remove}
                data-testid="pws-partner-logo-remove-btn"
                className="rounded-full text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
              >
                <Trash2 className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
            {/* Feb 2026 (production fix): the "Re-install default logo"
                affordance only surfaces when Mongo claims a logo but the
                image failed to load — the exact production symptom
                after preview→prod deploys that don't carry the disk
                files across. Never rendered when the logo is loading
                correctly, so the normal panel stays uncluttered. */}
            {logoBroken && (
              <Button
                size="sm"
                variant="outline"
                onClick={reinstallDefault}
                disabled={reinstalling}
                data-testid="pws-partner-logo-reinstall-default-btn"
                className="rounded-full text-xs bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                {reinstalling ? "Repairing…" : "Re-install default logo"}
              </Button>
            )}
          </div>
          <div className="text-[10px] text-slate-400">PNG, JPG, or WebP · max 1 MB. Shows on trade referral PDFs.</div>
          {logoBroken && (
            <div
              data-testid="pws-partner-logo-broken-banner"
              className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 leading-snug"
            >
              Logo metadata is set but the image file can&apos;t be loaded. Click <strong>Re-install default logo</strong> to restore the default PWS logo, or upload a custom one to replace it.
            </div>
          )}
          {branding?.logo_uploaded_at && !logoBroken && (
            <div className="text-[10px] text-slate-500">
              Uploaded {formatDateTimeAU(branding.logo_uploaded_at)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Feb 2026 — Trade Portal login invite panel ───────────────────────
// Feb 2026 — Login approval panel. Renders a status chip for the
// linked partner user (or "Not invited" if none), and shows a big
// "Approve login" button when the partner is stuck at
// `password_set_awaiting_approval` — the Reece @ PWS Cooloola bug
// class. Kept intentionally small so it only occupies drawer real
// estate when it's actionable.
function LoginApprovalPanel({ partnerId, userState, onApproved }) {
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const approve = async () => {
    if (!window.confirm(
      "Approve this partner's login? They'll be able to use every partner API immediately."
    )) return;
    setBusy(true);
    try {
      await api.post(`/admin/pws-partners/${partnerId}/approve-user`);
      toast.success("Login approved — banner will disappear on their next page load.");
      await onApproved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "object" ? detail?.message : detail;
      toast.error(message || "Failed to approve login");
    } finally {
      setBusy(false);
    }
  };

  const sendReset = async () => {
    if (!window.confirm(
      "Send a password-reset email to this partner's linked login address?\n\n" +
      "They'll get a fresh 60-minute link to set (or reset) their password. " +
      "Any existing reset links will still work."
    )) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/pws-partners/${partnerId}/send-password-reset`);
      const inbox = data?.sent_to || "the linked email";
      if (data?.email_delivered === false) {
        toast.warning(`Token minted but email delivery to ${inbox} failed — check email logs.`);
      } else {
        toast.success(`Reset link sent to ${inbox} (valid ${data?.ttl_minutes || 60} minutes).`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "object" ? detail?.message : detail;
      toast.error(message || "Failed to send reset");
    } finally {
      setSending(false);
    }
  };

  const state = userState || "not_invited";
  const isAwaiting = state === "password_set_awaiting_approval";
  // Reset can be triggered for any state where a login exists —
  // whether the partner has set a password or not. The only excluded
  // states are `not_invited` (no user) and `rejected`/`disabled`
  // (backend will 409 anyway).
  const canSendReset = ["invited_password_not_set",
                        "password_set_awaiting_approval",
                        "active"].includes(state);

  return (
    <div
      data-testid="pws-login-approval-panel"
      className="mt-3 rounded-2xl border border-slate-200 bg-white p-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
            Login status
          </div>
          <LoginStateChip state={state} />
          {isAwaiting && (
            <div className="text-xs text-amber-800 mt-2 max-w-md">
              Password is set but the login isn&apos;t approved yet.
              Every partner API returns 403 &ldquo;Account not approved&rdquo;
              until you click below.
            </div>
          )}
          {state === "invited_password_not_set" && (
            <div className="text-xs text-slate-500 mt-2">
              Invite email sent — waiting for the partner to open the link
              and set their password.
            </div>
          )}
          {state === "not_invited" && (
            <div className="text-xs text-slate-500 mt-2">
              No login provisioned yet. Send the Trade Portal invite above.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canSendReset && (
            <Button
              data-testid="pws-send-password-reset-btn"
              onClick={sendReset}
              disabled={sending}
              variant="outline"
              className="h-9"
              title="Mint a fresh reset link and email it to the partner's linked login address."
            >
              {sending ? "Sending…" : "Send password reset"}
            </Button>
          )}
          {isAwaiting && (
            <Button
              data-testid="pws-approve-login-btn"
              onClick={approve}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
            >
              {busy ? "Approving…" : "Approve login"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


// Feb 2026 — Landing page one-click provisioner. Renders a "Create
// landing page" button when the partner has none yet, or a
// status-pill + view/edit links when one already exists.
function LandingPagePanel({ partnerId, landingPage, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [row, setRow] = useState(landingPage);
  useEffect(() => setRow(landingPage), [landingPage]);

  const create = async () => {
    if (!window.confirm(
      "Create a draft landing page for this partner?\n\n" +
      "URL, business name, region, phone and email will be pre-filled " +
      "from the partner record. Status is 'draft' — nothing goes public " +
      "until you click Publish on the landing-pages screen."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/pws-partners/${partnerId}/create-landing-page`);
      setRow(data?.landing_page || null);
      toast.success(
        data?.already_exists
          ? `Landing page already exists at /pws-partner/${data.landing_page?.slug}.`
          : `Draft landing page created at /pws-partner/${data.landing_page?.slug}.`,
      );
      await onCreated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "object" ? detail?.message : detail;
      toast.error(message || "Failed to create landing page");
    } finally {
      setBusy(false);
    }
  };

  const statusPill = row?.status && {
    draft:     { bg: "bg-slate-100",   text: "text-slate-700",   label: "Draft" },
    published: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Published" },
    archived:  { bg: "bg-rose-100",    text: "text-rose-700",    label: "Archived" },
  }[row.status];

  return (
    <div
      data-testid="pws-landing-page-panel"
      className="mt-3 rounded-2xl border border-slate-200 bg-white p-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
            Landing page
          </div>
          {row ? (
            <div className="flex items-center gap-2 flex-wrap">
              {statusPill && (
                <span
                  data-testid={`pws-landing-status-chip-${row.status}`}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusPill.bg} ${statusPill.text}`}
                >
                  {statusPill.label}
                </span>
              )}
              <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded">
                /pws-partner/{row.slug}
              </code>
            </div>
          ) : (
            <div className="text-xs text-slate-500 max-w-md">
              No public landing page yet. One click below to create a
              draft pre-filled from partner data — you review + publish
              on the landing-pages screen.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {row ? (
            <>
              <a
                href={`/pws-partner/${row.slug}`}
                target="_blank"
                rel="noreferrer"
                data-testid="pws-landing-view-link"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                View
              </a>
              <a
                href="/admin/pws-partner-landing"
                data-testid="pws-landing-edit-link"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold bg-white border border-sky-300 text-sky-700 hover:bg-sky-50"
              >
                Edit / publish
              </a>
            </>
          ) : (
            <Button
              data-testid="pws-landing-create-btn"
              onClick={create}
              disabled={busy}
              className="bg-slate-900 hover:bg-cyan-700 text-white h-9"
            >
              {busy ? "Creating…" : "Create landing page"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}



// Feb 2026 — Bulk Kit Inventory manual-adjust panel for the PWS
// partner drawer. Wraps the existing endpoints:
//   GET  /api/admin/pws-partners/{id}/kit-inventory        — snapshot + history
//   POST /api/admin/pws-partners/{id}/kit-inventory/adjust — mutate
// UX: current balance (available / ordered / used) → direction picker
// (Increase / Decrease) → amount → mandatory reason → Save.
// Every save writes an audit row (actor_email + timestamp), and the
// panel refreshes the local snapshot + history without a page reload.
function KitInventoryPanel({ partnerId, partnerName, onAdjusted }) {
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState("increase");
  const [amount, setAmount] = useState("1");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchSnapshot = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/pws-partners/${partnerId}/kit-inventory`);
      setSnapshot({
        available: data.available || 0,
        ordered:   data.ordered   || 0,
        used:      data.used      || 0,
      });
      setHistory(Array.isArray(data.history) ? data.history : []);
    } catch (e) {
      // 404 shouldn't happen at this point (partner exists), but
      // stay quiet if it does.
      setSnapshot({ available: 0, ordered: 0, used: 0 });
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  const submit = async () => {
    const n = parseInt(amount, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Enter a whole-number amount greater than zero.");
      return;
    }
    const trimmed = (reason || "").trim();
    if (trimmed.length < 3) {
      toast.error("A reason (≥3 chars) is required.");
      return;
    }
    const delta = direction === "increase" ? n : -n;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/pws-partners/${partnerId}/kit-inventory/adjust`,
        { delta, reason: trimmed },
      );
      setSnapshot({
        available: data.available || 0,
        ordered:   data.ordered   || 0,
        used:      data.used      || 0,
      });
      setReason("");
      setAmount("1");
      toast.success(
        `Inventory ${direction === "increase" ? "increased" : "decreased"} by ${n}. Available: ${data.available}.`,
      );
      // Refresh history (audit row appended server-side).
      fetchSnapshot();
      if (onAdjusted) onAdjusted();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Could not adjust inventory. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const belowThreshold = (snapshot?.available ?? 0) < 3;

  return (
    <div
      data-testid="kit-inventory-panel"
      className="rounded-2xl bg-cyan-50/40 border border-cyan-200 p-4 mt-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Package className="w-4 h-4 text-cyan-700" />
        <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-800">
          Bulk Kit Inventory
        </div>
        {belowThreshold && (
          <span
            data-testid="kit-inventory-low-stock-chip"
            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800"
          >
            <AlertTriangle className="w-3 h-3" />
            Low stock (&lt; 3)
          </span>
        )}
      </div>

      {/* Snapshot tiles */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-lg bg-white border border-cyan-200 p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Available</div>
          <div data-testid="kit-inventory-available" className="text-2xl font-bold text-cyan-800 tabular-nums">
            {loading ? "…" : snapshot?.available ?? 0}
          </div>
        </div>
        <div className="rounded-lg bg-white border border-slate-200 p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ordered</div>
          <div data-testid="kit-inventory-ordered" className="text-2xl font-bold text-slate-700 tabular-nums">
            {loading ? "…" : snapshot?.ordered ?? 0}
          </div>
        </div>
        <div className="rounded-lg bg-white border border-slate-200 p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Used</div>
          <div data-testid="kit-inventory-used" className="text-2xl font-bold text-slate-700 tabular-nums">
            {loading ? "…" : snapshot?.used ?? 0}
          </div>
        </div>
      </div>

      {/* Adjust form */}
      <div className="rounded-lg bg-white border border-slate-200 p-3 space-y-2.5">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
          Manual adjustment
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="kit-inventory-direction-increase"
            onClick={() => setDirection("increase")}
            className={`text-left px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${
              direction === "increase"
                ? "border-emerald-500 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-500"
                : "border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            + Increase
          </button>
          <button
            type="button"
            data-testid="kit-inventory-direction-decrease"
            onClick={() => setDirection("decrease")}
            className={`text-left px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${
              direction === "decrease"
                ? "border-rose-500 bg-rose-50 text-rose-800 ring-1 ring-rose-500"
                : "border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            − Decrease
          </button>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount (kits)</Label>
          <Input
            data-testid="kit-inventory-amount"
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Reason (required)</Label>
          <Textarea
            data-testid="kit-inventory-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Booking SCWT-ABC123 cancelled — kit not used; damaged kit written off; stock-take correction."
            rows={2}
            className="mt-1 text-sm"
          />
        </div>
        <div className="flex justify-end">
          <Button
            data-testid="kit-inventory-save-btn"
            size="sm"
            disabled={busy}
            onClick={submit}
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
          >
            {busy ? "Saving…" : "Save adjustment"}
          </Button>
        </div>
      </div>

      {/* Audit history */}
      {history.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">
            Recent audit history
          </div>
          <div
            data-testid="kit-inventory-history"
            className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 max-h-56 overflow-y-auto"
          >
            {history.slice(0, 20).map((row) => (
              <div key={row.id} className="px-3 py-2 text-[12px]">
                <div className="flex items-center gap-2 justify-between">
                  <span className={`font-bold tabular-nums ${row.delta > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {row.delta > 0 ? "+" : ""}{row.delta}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    {formatDateTimeAU(row.created_at)}
                  </span>
                </div>
                <div className="text-slate-700 leading-snug mt-0.5">{row.reason}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {row.source} · {row.actor_email}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



// Feb 2026 — Rollout Setup panel. Closes the 4 previously-read-only
// pillars from the Rollout Overview chips (Booking, Branding,
// SLA, Sample Workflow) + surfaces Commission overrides + the
// Pickup/Drop-off toggle in one accordion-style panel inside the
// admin partner drawer. Each subsection saves independently through
// its own dedicated backend endpoint so a partial save (e.g. only
// the SLA hours) doesn't require dirty-tracking the whole form.
//
//   Booking / Referral   → PATCH /admin/pws-partners/{id}
//   Reporting & Branding → PATCH /admin/pws-partners/{id} + PUT /admin/partners/{id}/branding + POST logo
//   Service Level        → PATCH /admin/pws-partner-service-levels/{id}
//   Sample Collection    → PUT   /admin/pws-sample-collection/{id}
//   Commission           → PATCH /admin/pws-partners/{id}
//   Pickup / Drop-off    → PATCH /admin/pws-partners/{id}
function RolloutSetupPanel({ partner, onSaved }) {
  const [open, setOpen] = useState(null);   // null | "booking" | "branding" | "sla" | "sample" | "commission" | "pickup"
  const [forcedOpen, setForcedOpen] = useState(null);  // Set<string> from #rollout-fix=… — every listed section stays open
  const toggle = (k) => setOpen((cur) => (cur === k ? null : k));
  const isActive = partner.pws_partner_status === "active";

  // Feb 2026 — deep-link hash handler.
  //   #rollout-branding / #rollout-sla / #rollout-booking / #rollout-sample /
  //   #rollout-commission / #rollout-pickup → auto-open that one section.
  //   #rollout-fix=<key1>,<key2>,…                → auto-open EVERY listed pillar
  //   #business-details                            → scrolls to the profile
  //                                                  panel (handled elsewhere).
  // Chip → section key map (matches PWSPartnerOverview.jsx `configLink`).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) return;
    // Bulk-fix: #rollout-fix=branding,sla,sample_workflow,report_sla
    if (hash.startsWith("rollout-fix=")) {
      const raw = hash.slice("rollout-fix=".length);
      const keys = raw.split(",").map((k) => k.trim()).filter(Boolean);
      // Map Overview chip keys → panel section keys.
      const CHIP_TO_SECTION = {
        booking:         "booking",
        branding:        "branding",
        commission:      "commission",
        sla:             "sla",
        report_sla:      "sla",
        sample_workflow: "sample",
      };
      const sectionKeys = keys
        .map((k) => CHIP_TO_SECTION[k])
        .filter(Boolean);
      if (sectionKeys.length > 0) {
        setForcedOpen(new Set(sectionKeys));
        // Focus the first missing pillar so the admin lands in the
        // right place instead of at the top of the drawer.
        setOpen(sectionKeys[0]);
        setTimeout(() => {
          const first = document.querySelector(
            `[data-testid="rollout-section-body-${sectionKeys[0]}"]`,
          );
          if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
        return;
      }
    }
    // Single-section deep-link: `rollout-<section>`.
    const m = hash.match(/^rollout-(booking|branding|sla|sample|commission|pickup|fulfilment)$/);
    if (m) {
      setOpen(m[1]);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="rollout-section-body-${m[1]}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [partner.id]);

  const isOpen = (k) => open === k || (forcedOpen && forcedOpen.has(k));

  return (
    <div
      data-testid="rollout-setup-panel"
      className="rounded-2xl border border-cyan-200 bg-cyan-50/30 p-4 mt-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-cyan-700" />
        <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-800">
          Rollout Setup
        </div>
        <div className="ml-auto text-[10px] text-slate-500">
          Fields that flip the Rollout Overview chips to ✅
        </div>
      </div>

      {forcedOpen && forcedOpen.size > 0 && (
        <div
          data-testid="rollout-bulk-fix-banner"
          className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900"
        >
          <div className="font-semibold flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            {forcedOpen.size} pillar{forcedOpen.size === 1 ? "" : "s"} still need setup.
          </div>
          <div className="mt-0.5 opacity-80">
            All incomplete sections are expanded below — save each one to flip the chip green.
          </div>
        </div>
      )}

      <RolloutSection
        title="Booking fulfilment options" testKey="fulfilment" isOpen={isOpen("fulfilment")} onToggle={toggle}
      >
        <FulfilmentOptionsForm partner={partner} onSaved={onSaved} />
      </RolloutSection>

      <RolloutSection
        title="Booking / referral" testKey="booking" isOpen={isOpen("booking")} onToggle={toggle}
      >
        <BookingReferralForm partner={partner} isActive={isActive} onSaved={onSaved} />
      </RolloutSection>

      <RolloutSection
        title="Reporting & branding (white-label)" testKey="branding" isOpen={isOpen("branding")} onToggle={toggle}
      >
        <BrandingForm partner={partner} onSaved={onSaved} />
      </RolloutSection>

      <RolloutSection
        title="Service level (SLA)" testKey="sla" isOpen={isOpen("sla")} onToggle={toggle}
      >
        <ServiceLevelForm partner={partner} onSaved={onSaved} />
      </RolloutSection>

      <RolloutSection
        title="Local sample collection" testKey="sample" isOpen={isOpen("sample")} onToggle={toggle}
      >
        <SampleCollectionForm partner={partner} onSaved={onSaved} />
      </RolloutSection>

      <RolloutSection
        title="Commission" testKey="commission" isOpen={isOpen("commission")} onToggle={toggle}
      >
        <CommissionForm partner={partner} onSaved={onSaved} />
      </RolloutSection>

      <RolloutSection
        title="Pickup / drop-off (customer walk-in)" testKey="pickup" isOpen={isOpen("pickup")} onToggle={toggle}
      >
        <PickupDropoffForm partner={partner} onSaved={onSaved} />
      </RolloutSection>
    </div>
  );
}


function RolloutSection({ title, testKey, isOpen, onToggle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-2 overflow-hidden">
      <button
        type="button"
        data-testid={`rollout-section-toggle-${testKey}`}
        onClick={() => onToggle(testKey)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" />
                : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {isOpen && (
        <div
          data-testid={`rollout-section-body-${testKey}`}
          className="px-3 pb-3 pt-1 border-t border-slate-100"
        >
          {children}
        </div>
      )}
    </div>
  );
}


// ── Booking Fulfilment Options — 4 allow_* flags per partner ──────
function FulfilmentOptionsForm({ partner, onSaved }) {
  // Defaults per Feb 2026 spec: partner_supplied + mailout = TRUE,
  // lab_pickup + customer_dropoff = FALSE. Existing partners created
  // before this field existed also get the same defaults via the ??.
  const [allowPartner,  setAllowPartner]  = useState(partner.allow_partner_supplied_kit !== false);
  const [allowMailout,  setAllowMailout]  = useState(partner.allow_mailout_kit !== false);
  const [allowLab,      setAllowLab]      = useState(!!partner.allow_lab_pickup);
  const [allowDropoff,  setAllowDropoff]  = useState(!!partner.allow_customer_dropoff);
  const [busy, setBusy] = useState(false);
  // Feb 2026 — fetch the actual customer-facing prices from live
  // settings instead of hard-coding "$34" on the labels. The
  // partner-supplied fee comes from `booking_kit_settings.customer_partner_kit_charge_cents`
  // and the mail-out fee comes from `posted_kit_settings.fee_cents`;
  // both are already exposed to admins as read-only for other
  // surfaces.
  const [prices, setPrices] = useState({ partnerSupplied: 34, mailOut: 34 });
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/pre-booking/kit-source-options").then(r => r.data).catch(() => null),
      api.get("/public/posted-kit-settings").then(r => r.data).catch(() => null),
    ]).then(([kitSrc, postedKit]) => {
      if (cancelled) return;
      // Jun 2026 fix — a configured $0 is a real price, not "missing".
      // The old truthiness check made a deliberate $0 fall back to $34.
      const dollars = (v, fb) => (Number.isFinite(v) ? Math.round(v / 100) : fb);
      setPrices({
        partnerSupplied: dollars(kitSrc?.customer_charge_cents, 34),
        mailOut: dollars(postedKit?.fee_cents, 34),
      });
    });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/pws-partners/${partner.id}`, {
        allow_partner_supplied_kit: allowPartner,
        allow_mailout_kit:          allowMailout,
        allow_lab_pickup:           allowLab,
        allow_customer_dropoff:     allowDropoff,
      });
      toast.success("Booking fulfilment options saved");
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Save failed"));
    } finally { setBusy(false); }
  };

  const bothDisabled = !allowPartner && !allowMailout;

  return (
    <div className="space-y-2 text-sm">
      <div className="text-[11px] text-slate-500 mb-1">
        Controls which sample-kit fulfilment options this partner&apos;s customers see on the booking form.
      </div>
      <BoolRow
        label={`Allow Partner-Supplied Kits (${prices.partnerSupplied === 0 ? "free" : `$${prices.partnerSupplied}`})`}
        testKey="allow_partner_supplied_kit"
        checked={allowPartner}
        onChange={setAllowPartner}
        hint='Customers see "Already provided by my local PWS Partner" when this partner has kits in stock.'
      />
      <BoolRow
        label={`Allow Mail-Out Kits (${prices.mailOut === 0 ? "free" : `$${prices.mailOut}`})`}
        testKey="allow_mailout_kit"
        checked={allowMailout}
        onChange={setAllowMailout}
        hint="AWARE ships the kit direct to the customer. Standard fallback for public bookings."
      />
      <BoolRow
        label="Allow Laboratory Pickup"
        testKey="allow_lab_pickup"
        checked={allowLab}
        onChange={setAllowLab}
        hint="Optional — off by default. Only enable for partners physically near the Noosaville lab."
      />
      <BoolRow
        label="Allow Customer Bottle Drop-Off"
        testKey="allow_customer_dropoff"
        checked={allowDropoff}
        onChange={setAllowDropoff}
        hint="Optional — off by default. Allows the customer to bring their own bottle."
      />
      {bothDisabled && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900">
          ⚠ Both Partner-Supplied and Mail-Out are disabled — this partner&apos;s customers won&apos;t be able to complete a booking.
        </div>
      )}
      <div className="flex justify-end">
        <Button
          data-testid="rollout-fulfilment-save"
          size="sm" onClick={save} disabled={busy}
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
        >
          {busy ? "Saving…" : "Save fulfilment options"}
        </Button>
      </div>
    </div>
  );
}


// ── Booking / referral — partner_slug + receive_public_leads ─────────
function BookingReferralForm({ partner, isActive, onSaved }) {
  const [slug, setSlug] = useState(partner.partner_slug || "");
  const [receive, setReceive] = useState(!!partner.receive_public_leads);
  const [busy, setBusy] = useState(false);
  const slugLocked = isActive && !!(partner.partner_slug || "").trim();

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      const cleanSlug = (slug || "").trim().toUpperCase();
      if (cleanSlug !== (partner.partner_slug || "")) patch.partner_slug = cleanSlug;
      if (receive !== !!partner.receive_public_leads) patch.receive_public_leads = receive;
      if (Object.keys(patch).length === 0) {
        toast.info("Nothing to save.");
        setBusy(false); return;
      }
      await api.patch(`/admin/pws-partners/${partner.id}`, patch);
      toast.success("Booking settings saved");
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 text-sm">
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">
          Partner slug (public link)
        </Label>
        <Input
          data-testid="rollout-input-partner_slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toUpperCase())}
          placeholder="PWS-SUNSHINE-COAST"
          disabled={slugLocked}
          className="mt-1 h-9 font-mono uppercase"
        />
        <div className="text-[10px] text-slate-500 mt-1">
          {slugLocked
            ? "Slug is locked because the partner is active. Contact support to change it."
            : "Uppercase A–Z, digits and dashes. Used in booking URLs like /?p=PWS-SUNSHINE-COAST."}
        </div>
      </div>
      <BoolRow
        label="Receive public leads"
        testKey="receive_public_leads"
        checked={receive}
        onChange={setReceive}
        hint="When ON, region-match auto-allocation can allocate new customer bookings to this partner."
      />
      <div className="flex justify-end">
        <Button
          data-testid="rollout-booking-save"
          size="sm" onClick={save} disabled={busy}
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
        >
          {busy ? "Saving…" : "Save booking settings"}
        </Button>
      </div>
    </div>
  );
}


// ── Reporting & branding — white_label + brand_color + logo ────────
function BrandingForm({ partner, onSaved }) {
  const [wl, setWl] = useState(!!partner.white_label_enabled);
  const [brandName, setBrandName] = useState(partner.branding?.display_name || partner.business_name || "");
  const [color, setColor] = useState(partner.branding?.brand_color || partner.branding_brand_color || "#0e72ce");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoName = partner.branding?.logo_disk_filename || partner.branding_logo_url;
  // Feb 2026 — live preview logo URL. The partner branding record
  // stores the logo as a base64 data URL (`branding.logo_data_url`)
  // which we can safely render in an `<img>` tag without needing an
  // auth header. Falls back to null (placeholder) when only the
  // legacy on-disk filename is present.
  const logoUrl = partner.branding?.logo_data_url || null;

  const save = async () => {
    setBusy(true);
    try {
      // 1. white_label_enabled → PATCH /admin/pws-partners
      if (wl !== !!partner.white_label_enabled) {
        await api.patch(`/admin/pws-partners/${partner.id}`, { white_label_enabled: wl });
      }
      // 2. brand_color + display_name → PUT /admin/partners/{id}/branding
      await api.put(`/admin/partners/${partner.id}/branding`, {
        display_name: brandName,
        brand_color:  color,
      });
      toast.success("Branding saved");
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Save failed"));
    } finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // Feb 2026 — backend endpoint (backend/routers/partner_branding.py
      // line 261) declares `file: UploadFile = File(...)`. Sending the
      // file under any other field name (e.g. "logo") returns 422 and
      // the toast shows "Upload failed".
      const fd = new FormData();
      fd.append("file", file);
      await api.post(
        `/admin/partners/${partner.id}/branding/logo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Logo uploaded");
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Upload failed"));
    } finally { setUploading(false); }
  };

  const deleteLogo = async () => {
    if (!window.confirm("Remove the current logo?")) return;
    try {
      await api.delete(`/admin/partners/${partner.id}/branding/logo`);
      toast.success("Logo removed");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-3 text-sm">
      <BoolRow
        label="White-label enabled"
        testKey="white_label_enabled"
        checked={wl}
        onChange={setWl}
        hint="Applies to the customer Summary Report only. The COA always stays SCWT / Aware-branded regardless of this setting."
      />
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Display name on reports</Label>
        <Input
          data-testid="rollout-input-display_name"
          value={brandName}
          onChange={(e) => setBrandName(e.target.value)}
          placeholder="Pristine Water Systems Sunshine Coast"
          className="mt-1 h-9"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Brand colour</Label>
        <div className="mt-1 flex items-center gap-2">
          <input
            data-testid="rollout-input-brand_color-picker"
            type="color"
            value={color.startsWith("#") ? color : `#${color}`}
            onChange={(e) => setColor(e.target.value)}
            className="w-10 h-9 rounded border border-slate-200 cursor-pointer"
          />
          <Input
            data-testid="rollout-input-brand_color-hex"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            placeholder="#0e72ce"
            className="h-9 font-mono flex-1"
          />
        </div>
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Logo</Label>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          {logoName && (
            <div className="text-[11px] text-slate-600 bg-slate-100 rounded px-2 py-1 font-mono">
              {String(logoName).split("/").pop()}
            </div>
          )}
          <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
            <Upload className="w-3.5 h-3.5" />
            {uploading ? "Uploading…" : (logoName ? "Replace logo" : "Upload logo")}
            <input
              type="file" accept="image/*"
              data-testid="rollout-input-logo"
              className="hidden"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
            />
          </label>
          {logoName && (
            <Button
              data-testid="rollout-branding-logo-delete"
              size="sm" variant="outline" onClick={deleteLogo}
            >
              Remove
            </Button>
          )}
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          PNG / JPG / SVG, up to 2 MB. Appears on the customer Summary Report when white-label is on.
        </div>
      </div>

      {/* Feb 2026 — live preview mock of the customer Summary Report
          header. Updates instantly as the admin changes brand colour /
          display name / logo. Not connected to the real PDF renderer —
          just a visual affordance so we don't ship a logo that looks
          terrible against the accent colour. */}
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Live preview</Label>
        <div
          data-testid="rollout-branding-preview"
          className="mt-1 rounded-lg border border-slate-200 bg-white overflow-hidden"
          style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
        >
          {/* Coloured header bar mimics the Summary Report cover strip. */}
          <div
            className="h-2"
            style={{ backgroundColor: color.startsWith("#") ? color : `#${color}` }}
          />
          <div className="p-3 flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                data-testid="rollout-branding-preview-logo"
                className="h-10 w-auto max-w-[120px] object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="h-10 w-10 rounded bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-[9px] text-slate-400 font-semibold">
                LOGO
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Water Quality Summary Report
              </div>
              <div
                data-testid="rollout-branding-preview-name"
                className="text-sm font-bold truncate"
                style={{ color: color.startsWith("#") ? color : `#${color}` }}
              >
                {wl ? (brandName || "Your Partner") : "Aware Water Testing"}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Sample ref · SCWT-XXXXXX · Prepared for J. Sample
              </div>
            </div>
          </div>
          <div className="px-3 pb-3">
            <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: "68%",
                  backgroundColor: color.startsWith("#") ? color : `#${color}`,
                }}
              />
            </div>
            <div className="mt-1 text-[10px] text-slate-500">
              Compliance score preview · uses your accent colour.
            </div>
          </div>
        </div>
        <div className="text-[10px] text-slate-500 mt-1">
          When white-label is <b>{wl ? "ON" : "OFF"}</b>, customers see{" "}
          <b>{wl ? (brandName || "your partner name") : "Aware Water Testing"}</b> at the top of the report.
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          data-testid="rollout-branding-save"
          size="sm" onClick={save} disabled={busy}
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
        >
          {busy ? "Saving…" : "Save branding"}
        </Button>
      </div>
    </div>
  );
}


// ── Service Level (SLA) ─────────────────────────────────────────────
function ServiceLevelForm({ partner, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/pws-partner-service-levels/${partner.id}`);
        // Server wraps the mutable fields under a `settings` object.
        const s = data?.settings || {};
        setSettings({
          service_level_enabled:             !!s.service_level_enabled,
          expected_response_hours:           s.expected_response_hours ?? "",
          expected_follow_up_hours:          s.expected_follow_up_hours ?? "",
          expected_opportunity_review_hours: s.expected_opportunity_review_hours ?? "",
          expected_customer_contact_hours:   s.expected_customer_contact_hours ?? "",
          expected_quote_follow_up_hours:    s.expected_quote_follow_up_hours ?? "",
          service_level_note:                s.service_level_note || "",
        });
      } catch {
        setSettings({
          service_level_enabled: false,
          expected_response_hours: "", expected_follow_up_hours: "",
          expected_opportunity_review_hours: "", expected_customer_contact_hours: "",
          expected_quote_follow_up_hours: "", service_level_note: "",
        });
      }
    })();
  }, [partner.id]);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!settings) return;
    if ((reason || "").trim().length < 3) {
      toast.error("Please add a short reason for the audit log.");
      return;
    }
    setBusy(true);
    try {
      const patch = { reason };
      for (const k of [
        "service_level_enabled", "service_level_note",
        "expected_response_hours", "expected_follow_up_hours",
        "expected_opportunity_review_hours", "expected_customer_contact_hours",
        "expected_quote_follow_up_hours",
      ]) {
        const v = settings[k];
        if (v === "" || v === null || v === undefined) continue;
        if (typeof v === "number" || typeof v === "boolean") { patch[k] = v; continue; }
        // numeric strings → ints
        if (k.startsWith("expected_")) {
          const n = parseInt(v, 10);
          if (Number.isFinite(n) && n >= 1 && n <= 720) patch[k] = n;
        } else {
          patch[k] = v;
        }
      }
      await api.patch(`/admin/pws-partner-service-levels/${partner.id}`, patch);
      toast.success("SLA settings saved");
      setReason("");
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Save failed"));
    } finally { setBusy(false); }
  };

  if (!settings) return <div className="text-xs text-slate-500 py-2">Loading…</div>;

  return (
    <div className="space-y-3 text-sm">
      <BoolRow
        label="Track service-level expectations"
        testKey="service_level_enabled"
        checked={settings.service_level_enabled}
        onChange={(v) => set("service_level_enabled", v)}
      />
      <div className="grid grid-cols-2 gap-2">
        <SLAField label="Response (h)"        testKey="response"           value={settings.expected_response_hours}           onChange={(v) => set("expected_response_hours", v)} />
        <SLAField label="Follow-up (h)"       testKey="follow_up"          value={settings.expected_follow_up_hours}          onChange={(v) => set("expected_follow_up_hours", v)} />
        <SLAField label="Opp. review (h)"     testKey="opportunity_review" value={settings.expected_opportunity_review_hours} onChange={(v) => set("expected_opportunity_review_hours", v)} />
        <SLAField label="Cust. contact (h)"   testKey="customer_contact"   value={settings.expected_customer_contact_hours}   onChange={(v) => set("expected_customer_contact_hours", v)} />
        <SLAField label="Quote follow-up (h)" testKey="quote_follow_up"    value={settings.expected_quote_follow_up_hours}    onChange={(v) => set("expected_quote_follow_up_hours", v)} />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Internal note (optional)</Label>
        <Textarea
          data-testid="rollout-input-sla-note"
          value={settings.service_level_note}
          onChange={(e) => set("service_level_note", e.target.value)}
          rows={2}
          className="mt-1 text-sm"
          placeholder="e.g. partner on annual leave 15-31 Aug — response expectations relaxed"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason (required, audit log)</Label>
        <Input
          data-testid="rollout-input-sla-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. initial SLA setup — 24/48/48/24/48 hours"
          className="mt-1 h-9"
        />
      </div>
      <div className="flex justify-end">
        <Button
          data-testid="rollout-sla-save"
          size="sm" onClick={save} disabled={busy}
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
        >
          {busy ? "Saving…" : "Save SLA settings"}
        </Button>
      </div>
    </div>
  );
}

function SLAField({ label, testKey, value, onChange }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Input
        data-testid={`rollout-input-sla-${testKey}`}
        type="number" min="1" max="720" step="1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9"
      />
    </div>
  );
}


// ── Local sample collection ─────────────────────────────────────────
function SampleCollectionForm({ partner, onSaved }) {
  const [enabled, setEnabled]     = useState(!!partner.local_sample_collection_enabled);
  const [postcodes, setPostcodes] = useState(
    (partner.local_sample_collection_postcodes || []).join(", "),
  );
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const list = postcodes
        .split(/[\s,;\n]+/)
        .map((p) => p.trim())
        .filter(Boolean);
      await api.put(`/admin/pws-sample-collection/${partner.id}`, {
        local_sample_collection_enabled: enabled,
        local_sample_collection_postcodes: list,
      });
      toast.success("Sample collection saved");
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 text-sm">
      <BoolRow
        label="Local sample collection enabled"
        testKey="local_sample_collection_enabled"
        checked={enabled}
        onChange={setEnabled}
        hint="When ON, customers whose postcode matches this list see this partner as a local drop-off point on the booking form."
      />
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">
          Serviced postcodes
        </Label>
        <Textarea
          data-testid="rollout-input-sample-postcodes"
          value={postcodes}
          onChange={(e) => setPostcodes(e.target.value)}
          rows={3}
          placeholder="4566, 4567, 4568, 4573"
          className="mt-1 text-sm font-mono"
        />
        <div className="text-[10px] text-slate-500 mt-1">
          Comma / space / newline separated. Only 4-digit AU postcodes are accepted; anything else is stripped.
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          data-testid="rollout-sample-save"
          size="sm" onClick={save} disabled={busy}
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
        >
          {busy ? "Saving…" : "Save sample collection"}
        </Button>
      </div>
    </div>
  );
}


// ── Commission ─────────────────────────────────────────────────────
function CommissionForm({ partner, onSaved }) {
  const [enabled, setEnabled] = useState(partner.commission_enabled !== false);
  const [regional, setRegional] = useState(!!partner.regional_commission_enabled);
  const [amount, setAmount]     = useState(partner.commission_amount ?? "");
  const [percent, setPercent]   = useState(partner.commission_percent ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        commission_enabled:          enabled,
        regional_commission_enabled: regional,
      };
      if (amount !== "" && amount !== null) {
        const n = parseFloat(amount);
        if (Number.isFinite(n) && n >= 0) patch.commission_amount = n;
      } else {
        patch.commission_amount = null;
      }
      if (percent !== "" && percent !== null) {
        const n = parseFloat(percent);
        if (Number.isFinite(n) && n >= 0 && n <= 100) patch.commission_percent = n;
      } else {
        patch.commission_percent = null;
      }
      await api.patch(`/admin/pws-partners/${partner.id}`, patch);
      toast.success("Commission settings saved");
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 text-sm">
      <BoolRow
        label="Commission enabled"
        testKey="commission_enabled"
        checked={enabled}
        onChange={setEnabled}
        hint="When OFF, no new commission ledger entries are created for bookings attributed to this partner."
      />
      <BoolRow
        label="Regional commission enabled"
        testKey="regional_commission_enabled"
        checked={regional}
        onChange={setRegional}
        hint="Turn on for franchisees that earn commission on region-allocated bookings, not just their own direct links."
      />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Flat amount ($, optional)</Label>
          <Input
            data-testid="rollout-input-commission_amount"
            type="number" min="0" step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 25.00"
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Percent (0–100, optional)</Label>
          <Input
            data-testid="rollout-input-commission_percent"
            type="number" min="0" max="100" step="0.1"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            placeholder="e.g. 15"
            className="mt-1 h-9"
          />
        </div>
      </div>
      <div className="text-[10px] text-slate-500">
        Ledger caps every entry at the booking&apos;s `partner_margin_allowance` regardless of these overrides.
      </div>
      <div className="flex justify-end">
        <Button
          data-testid="rollout-commission-save"
          size="sm" onClick={save} disabled={busy}
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
        >
          {busy ? "Saving…" : "Save commission"}
        </Button>
      </div>
    </div>
  );
}


// ── Pickup / drop-off ────────────────────────────────────────────────
function PickupDropoffForm({ partner, onSaved }) {
  const [enabled, setEnabled] = useState(!!partner.pickup_dropoff_enabled);
  const [addr, setAddr]       = useState(partner.pickup_dropoff_address || "");
  const [instr, setInstr]     = useState(partner.pickup_dropoff_instructions || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/pws-partners/${partner.id}`, {
        pickup_dropoff_enabled:      enabled,
        pickup_dropoff_address:      addr,
        pickup_dropoff_instructions: instr,
      });
      toast.success("Pickup / drop-off saved");
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 text-sm">
      <BoolRow
        label="Pickup / drop-off enabled"
        testKey="pickup_dropoff_enabled"
        checked={enabled}
        onChange={setEnabled}
        hint="When ON, customers can pick up a bottle from OR drop off their sample to this partner's business address on the booking page."
      />
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Address</Label>
        <Input
          data-testid="rollout-input-pickup_address"
          value={addr}
          onChange={(e) => setAddr(e.target.value)}
          placeholder="Unit 1 / 37 Gateway Drive, Noosaville QLD 4566"
          className="mt-1 h-9"
        />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Customer-facing instructions</Label>
        <Textarea
          data-testid="rollout-input-pickup_instructions"
          value={instr}
          onChange={(e) => setInstr(e.target.value)}
          rows={2}
          placeholder="Open Mon–Fri 8am–5pm. Ask at reception for the Aware sample kit or drop-off box."
          className="mt-1 text-sm"
        />
      </div>
      <div className="flex justify-end">
        <Button
          data-testid="rollout-pickup-save"
          size="sm" onClick={save} disabled={busy}
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
        >
          {busy ? "Saving…" : "Save pickup / drop-off"}
        </Button>
      </div>
    </div>
  );
}


// Reusable boolean toggle row.
function BoolRow({ label, testKey, checked, onChange, hint }) {
  return (
    <div className="flex items-start gap-3 p-2 rounded-lg bg-slate-50 border border-slate-100">
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          data-testid={`rollout-toggle-${testKey}`}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
        />
        <span className="text-sm font-medium text-slate-800">{label}</span>
      </label>
      {hint && <div className="text-[10px] text-slate-500 flex-1 mt-0.5">{hint}</div>}
    </div>
  );
}



// Provisions a users row + sends a 7-day password setup email for the
// partner. Only meaningful for portal-eligible partner types
// (pws_franchisee today). Renders itself as a calm sky-blue card with
// the current invite status + Send / Resend button + orphan-user
// confirm dialog if the API 409s with `existing_user_orphan`.
// ─── Phase 2 — Partner Booking Link Panel ───────────────────────────────
function PartnerBookingLinkPanel({ partner }) {
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugDraft, setSlugDraft] = useState(partner.partner_slug || "");
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const isActive = (partner.pws_partner_status || "draft") === "active";
  const slug = (partner.partner_slug || "").trim();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const link = slug ? `${origin}/book-water-test?p=${encodeURIComponent(slug)}` : "";

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Booking link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Unable to copy. Select the link manually.");
    }
  };

  const saveSlug = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/pws-partners/${partner.id}`, {
        partner_slug: slugDraft.trim(),
      });
      toast.success("Slug updated — no email was sent.");
      setEditingSlug(false);
      // Trigger refetch in the drawer parent by reloading the route
      window.dispatchEvent(new CustomEvent("pws-partner-updated"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save slug");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="pws-booking-link-panel"
      className="mt-5 rounded-2xl border border-cyan-200 bg-cyan-50/40 p-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="w-4 h-4 text-cyan-700" />
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
          Partner booking link
        </div>
        {isActive ? (
          <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 uppercase tracking-wider">
            Live
          </span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-600 uppercase tracking-wider">
            Preview · not live until active
          </span>
        )}
      </div>

      {!editingSlug ? (
        <>
          <div className="flex items-center gap-2 mb-2">
            <code
              data-testid="pws-partner-slug-display"
              className="text-xs font-mono px-2 py-1 rounded bg-white border border-cyan-200 text-cyan-900"
            >
              {slug || "— (not set)"}
            </code>
            <button
              data-testid="pws-edit-slug-btn"
              onClick={() => { setSlugDraft(slug); setEditingSlug(true); }}
              disabled={isActive}
              title={isActive ? "Pause the partner before renaming the slug" : ""}
              className="text-[11px] text-cyan-700 hover:text-cyan-800 underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
            >
              {isActive ? "Locked while active" : "Edit slug"}
            </button>
          </div>
          {slug && (
            <div className="flex items-center gap-2">
              <code
                data-testid="pws-booking-link-url"
                className="flex-1 text-[11px] font-mono px-3 py-2 rounded-lg bg-white border border-cyan-200 text-slate-700 overflow-x-auto whitespace-nowrap"
              >
                {link}
              </code>
              <Button
                data-testid="pws-copy-booking-link-btn"
                onClick={copyLink}
                variant="outline"
                size="sm"
                className="border-cyan-300 text-cyan-800 hover:bg-cyan-100"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
              </Button>
            </div>
          )}
          <div className="text-[10px] text-slate-500 mt-2">
            Customers who land via this URL will have the booking attributed to this
            franchisee. <strong>Only active partner slugs resolve;</strong> a draft or
            paused slug returns the normal anonymous booking flow.
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            data-testid="pws-edit-slug-input"
            value={slugDraft}
            onChange={(e) => setSlugDraft(e.target.value)}
            placeholder="PWS-NOOSA"
            className="font-mono uppercase tracking-wider"
          />
          <Button
            data-testid="pws-save-slug-btn"
            onClick={saveSlug}
            disabled={saving}
            size="sm"
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            onClick={() => { setEditingSlug(false); setSlugDraft(slug); }}
            disabled={saving}
            variant="outline"
            size="sm"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

function EditForm({ form, setForm, onCancel, onSave, busy, hasLinkedPortalUser, currentLoginEmail }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const emailChanged = hasLinkedPortalUser
    && (form.email || "").trim().toLowerCase() !== (currentLoginEmail || "").trim().toLowerCase();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label>Business name</Label>
          <Input data-testid="pws-edit-business-name" value={form.business_name} onChange={set("business_name")} />
        </div>
        <div>
          <Label>Contact name</Label>
          <Input data-testid="pws-edit-contact-name" value={form.contact_name} onChange={set("contact_name")} />
        </div>
        <div>
          <Label>Contact email</Label>
          <Input
            data-testid="pws-edit-email"
            type="email"
            value={form.email || ""}
            onChange={set("email")}
            placeholder="partner@example.com"
          />
          {emailChanged && (
            <div
              data-testid="pws-edit-email-linked-warning"
              className="mt-1.5 rounded-md bg-amber-50 border border-amber-200 p-2 text-[10px] text-amber-900 leading-snug"
            >
              <strong>Contact email only.</strong> This partner has a linked Trade Portal login (<code className="bg-white px-1 rounded">{currentLoginEmail}</code>). Saving here will NOT change the portal login. Use <em>Update login email</em> in the Trade Portal invite panel to move the login.
            </div>
          )}
        </div>
        <div>
          <Label>Phone</Label>
          <Input data-testid="pws-edit-phone" value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <Label>Website</Label>
          <Input data-testid="pws-edit-website" value={form.website} onChange={set("website")} />
        </div>
        <div>
          <Label>Franchise region</Label>
          <Input data-testid="pws-edit-region" value={form.franchise_region} onChange={set("franchise_region")} />
        </div>
        <div>
          <Label>State</Label>
          <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
            <SelectTrigger data-testid="pws-edit-state"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AU_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>ABN</Label>
          <Input data-testid="pws-edit-abn" value={form.abn} onChange={set("abn")} />
        </div>
        <div className="sm:col-span-2">
          <Label>Office address</Label>
          <Input data-testid="pws-edit-office-address" value={form.office_address} onChange={set("office_address")} />
        </div>
        <div className="sm:col-span-2">
          <Label>Service postcodes</Label>
          <Input data-testid="pws-edit-postcodes" value={form.service_postcodes} onChange={set("service_postcodes")} />
        </div>
        <div className="sm:col-span-2">
          <Label>Service suburbs</Label>
          <Input data-testid="pws-edit-suburbs" value={form.service_suburbs} onChange={set("service_suburbs")} />
        </div>
        <div>
          <Label>Commission %</Label>
          <Input
            data-testid="pws-edit-commission"
            type="number" min={0} max={100} step={0.5}
            value={form.commission_pct}
            onChange={set("commission_pct")}
          />
        </div>
        <div>
          <Label>Mail-out enabled</Label>
          <Select
            value={form.mail_out_enabled ? "true" : "false"}
            onValueChange={(v) => setForm((f) => ({ ...f, mail_out_enabled: v === "true" }))}
          >
            <SelectTrigger data-testid="pws-edit-mailout"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Yes</SelectItem>
              <SelectItem value="false">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label>Notes</Label>
          <Textarea data-testid="pws-edit-notes" rows={3} value={form.notes} onChange={set("notes")} />
        </div>
      </div>
      <div className="mt-2 flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
        <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button data-testid="pws-edit-save-btn" onClick={onSave} disabled={busy} className="btn-aqua font-semibold">
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Admin bank details section (Feb 2026 P0 #1)
// ═══════════════════════════════════════════════════════════════════

function AdminBankStatusChip({ status }) {
  const map = {
    complete:   { pill: "bg-emerald-100 text-emerald-700", label: "Complete" },
    incomplete: { pill: "bg-amber-100 text-amber-800",     label: "Incomplete" },
    missing:    { pill: "bg-slate-100 text-slate-500",     label: "Not set up" },
  };
  const s = map[status] || map.missing;
  return (
    <span
      data-testid={`pws-drawer-bank-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.pill}`}
    >
      {s.label}
    </span>
  );
}

function AdminBankDetailsSection({ partnerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revealing, setRevealing] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [showVerifyNote, setShowVerifyNote] = useState(false);
  const [verifyNote, setVerifyNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_BANK_FORM);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(
        `/admin/partners/${encodeURIComponent(partnerId)}/bank-details`,
      );
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => { load(); }, [load]);

  const reveal = async () => {
    if (!window.confirm(
      "Reveal this partner's full bank details? This action is audit-logged."
    )) return;
    setRevealing(true);
    try {
      const { data } = await api.get(
        `/admin/partners/${encodeURIComponent(partnerId)}/bank-details?reveal=true`,
      );
      setData(data);
      toast.success("Details revealed. Audit row written.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setRevealing(false);
    }
  };

  const hide = () => { load(); };

  const loadAudit = async () => {
    try {
      const { data } = await api.get(
        `/admin/partners/${encodeURIComponent(partnerId)}/bank-details/audit`,
      );
      setAudit(data);
      setShowAudit(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const doVerify = async () => {
    setVerifying(true);
    try {
      const { data: fresh } = await api.post(
        `/admin/partners/${encodeURIComponent(partnerId)}/bank-details/verify`,
        { note: verifyNote || null },
      );
      setData((prev) => ({ ...(prev || {}), ...fresh, revealed: prev?.revealed }));
      setShowVerifyNote(false);
      setVerifyNote("");
      toast.success("Marked as verified. Audit row written.");
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || e.message);
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  const doUnverify = async () => {
    if (!window.confirm(
      "Clear the verified flag for this partner's bank details? An audit row will be written."
    )) return;
    setVerifying(true);
    try {
      const { data: fresh } = await api.post(
        `/admin/partners/${encodeURIComponent(partnerId)}/bank-details/unverify`,
      );
      setData((prev) => ({ ...(prev || {}), ...fresh, revealed: prev?.revealed }));
      toast.success("Verified flag cleared. Audit row written.");
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || e.message);
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  const isRevealed = !!data?.revealed;
  const isVerified = !!data?.verified;
  const canVerify = data?.status === "complete";

  const openFormFromData = (d) => {
    setForm({
      account_name: d?.revealed?.account_name || "",
      bsb: d?.bsb || "",
      account_number: d?.revealed?.account_number || "",
      payid: d?.revealed?.payid || "",
      remittance_email: d?.revealed?.remittance_email || "",
      notes: d?.revealed?.notes || "",
    });
    setFieldError(null);
    setEditing(true);
  };

  const startAdd = () => {
    setForm(EMPTY_BANK_FORM);
    setFieldError(null);
    setEditing(true);
  };

  const startEdit = async () => {
    if (isRevealed) {
      openFormFromData(data);
      return;
    }
    if (!window.confirm(
      "Reveal this partner's full bank details to edit them? This action is audit-logged."
    )) return;
    setRevealing(true);
    try {
      const { data: fresh } = await api.get(
        `/admin/partners/${encodeURIComponent(partnerId)}/bank-details?reveal=true`,
      );
      setData(fresh);
      openFormFromData(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setRevealing(false);
    }
  };

  const cancelForm = () => {
    setEditing(false);
    setFieldError(null);
  };

  const saveForm = async () => {
    setSaving(true);
    setFieldError(null);
    try {
      await api.put(
        `/admin/partners/${encodeURIComponent(partnerId)}/bank-details`,
        form,
      );
      setEditing(false);
      toast.success("Bank details saved.");
      load();
    } catch (e) {
      const status = e.response?.status;
      const d = e.response?.data?.detail;
      if (status === 422 && Array.isArray(d) && d.length > 0) {
        // FastAPI/Pydantic field-level validation error list.
        const first = d[0];
        const loc = Array.isArray(first?.loc) ? first.loc.filter((p) => p !== "body") : [];
        setFieldError({
          field: loc[loc.length - 1] || "form",
          message: first?.msg || "Invalid value",
        });
      } else if (d && typeof d === "object" && d.field) {
        // Our own custom 400 ValidationError shape.
        setFieldError(d);
      } else {
        toast.error(typeof d === "string" ? d : e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="pws-drawer-bank-details"
      className="mt-6 border-t border-slate-100 pt-5"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
            Bank details for payouts
          </div>
          {data && <AdminBankStatusChip status={data.status} />}
        </div>
        <button
          type="button"
          data-testid="pws-drawer-bank-audit-btn"
          onClick={loadAudit}
          className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
        >
          Audit log →
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400">Loading…</div>
      ) : error ? (
        <div data-testid="pws-drawer-bank-error" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">
          {error}
        </div>
      ) : editing ? (
        <BankDetailsForm
          form={form}
          setForm={setForm}
          fieldError={fieldError}
          saving={saving}
          onSave={saveForm}
          onCancel={cancelForm}
        />
      ) : !data?.has_bank_details ? (
        <div
          data-testid="pws-drawer-bank-empty"
          className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"
        >
          <span className="font-semibold">Not set up yet.</span>{" "}
          Ask this partner to add bank details in their portal at
          <span className="font-mono"> /partner/profile</span>, or add them on their
          behalf below. Payout runs can still be created,
          but you&apos;ll need the details before the money can be transferred.
          <div className="mt-2">
            <button
              type="button"
              data-testid="pws-drawer-bank-add-btn"
              onClick={startAdd}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-amber-700 hover:bg-amber-800 transition-colors"
            >
              Add bank details
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <BankSummaryField
              label="Account name"
              value={isRevealed ? data.revealed?.account_name : "•••••"}
              testid="pws-drawer-bank-account-name"
            />
            <BankSummaryField
              label="BSB"
              value={data.bsb}
              testid="pws-drawer-bank-bsb"
            />
            <BankSummaryField
              label="Account number"
              value={isRevealed ? data.revealed?.account_number : data.account_number_masked}
              testid="pws-drawer-bank-account-number"
            />
            <BankSummaryField
              label="PayID"
              value={isRevealed ? (data.revealed?.payid || "—") : (data.revealed?.payid ? "•••" : "—")}
              testid="pws-drawer-bank-payid"
            />
            <BankSummaryField
              label="Remittance email"
              value={isRevealed ? (data.revealed?.remittance_email || "—") : (data.revealed?.remittance_email ? "•••@•••" : "—")}
              testid="pws-drawer-bank-remittance-email"
            />
            <BankSummaryField
              label="Notes"
              value={isRevealed ? (data.revealed?.notes || "—") : (data.revealed?.notes ? "•••" : "—")}
              testid="pws-drawer-bank-notes"
            />
          </div>
          <div className="mt-3 text-[11px] text-slate-500">
            Last updated {new Date(data.last_updated_at).toLocaleString("en-AU")} by {data.last_updated_by_email}
            {" "}({data.last_updated_by_role}).
          </div>

          {/* Verified toggle — Feb 2026 P0 #2 ─────────────────────── */}
          <div
            data-testid="pws-drawer-bank-verified-panel"
            className={`mt-3 rounded-lg border p-2.5 flex items-start justify-between gap-3 ${
              isVerified
                ? "bg-emerald-50 border-emerald-200"
                : "bg-amber-50 border-amber-200"
            }`}
          >
            <div className="min-w-0">
              <div className={`text-xs font-bold ${
                isVerified ? "text-emerald-800" : "text-amber-900"
              }`}>
                Bank details verified:{" "}
                <span data-testid="pws-drawer-bank-verified-status">
                  {isVerified ? "Yes" : "No / Not verified"}
                </span>
              </div>
              {isVerified ? (
                <div className="text-[11px] text-emerald-700 mt-0.5">
                  {data.verified_at && (
                    <>Verified {new Date(data.verified_at).toLocaleString("en-AU")}
                     {data.verified_by_email && <> by {data.verified_by_email}</>}
                    </>
                  )}
                  {data.verification_note && (
                    <div className="text-[10px] italic text-emerald-800 mt-0.5">
                      Note: {data.verification_note}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-amber-700 mt-0.5">
                  Verify only after you&apos;ve independently confirmed the account details
                  (e.g. deposit slip, test transfer receipt, partner call-back).
                </div>
              )}
            </div>
            {!isVerified ? (
              canVerify ? (
                <button
                  type="button"
                  data-testid="pws-drawer-bank-verify-btn"
                  onClick={() => setShowVerifyNote(true)}
                  disabled={verifying}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  Mark verified
                </button>
              ) : (
                <span
                  className="shrink-0 text-[10px] text-amber-800 italic"
                  data-testid="pws-drawer-bank-verify-unavailable"
                >
                  Complete details required
                </span>
              )
            ) : (
              <button
                type="button"
                data-testid="pws-drawer-bank-unverify-btn"
                onClick={doUnverify}
                disabled={verifying}
                className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 disabled:opacity-50 transition-colors"
              >
                Clear verified
              </button>
            )}
          </div>

          {showVerifyNote && (
            <div
              data-testid="pws-drawer-bank-verify-note-panel"
              className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5"
            >
              <label className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">
                Optional note (visible in the audit log + statement)
              </label>
              <input
                type="text"
                maxLength={200}
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
                placeholder="e.g. confirmed via test transfer 05 Feb 2026"
                data-testid="pws-drawer-bank-verify-note-input"
                className="mt-1 w-full px-2.5 py-1.5 rounded-lg border border-emerald-300 text-xs focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none bg-white"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowVerifyNote(false); setVerifyNote(""); }}
                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-2 py-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="pws-drawer-bank-verify-confirm-btn"
                  onClick={doVerify}
                  disabled={verifying}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {verifying ? "Saving…" : "Confirm verified"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            {!isRevealed ? (
              <button
                type="button"
                data-testid="pws-drawer-bank-reveal-btn"
                onClick={reveal}
                disabled={revealing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-slate-800 hover:bg-slate-900 disabled:opacity-50 transition-colors"
              >
                {revealing ? "Revealing…" : "Reveal full details (audit-logged)"}
              </button>
            ) : (
              <button
                type="button"
                data-testid="pws-drawer-bank-hide-btn"
                onClick={hide}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                Hide details
              </button>
            )}
            <button
              type="button"
              data-testid="pws-drawer-bank-edit-btn"
              onClick={startEdit}
              disabled={revealing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 disabled:opacity-50 transition-colors"
            >
              Edit details
            </button>
          </div>
          {isVerified && (
            <div className="mt-2 text-[10px] text-amber-700 italic">
              Changing account name, BSB, account number or PayID will clear the
              verified flag and require re-verification. Remittance email and notes
              changes don&apos;t affect verification.
            </div>
          )}
        </div>
      )}

      {showAudit && audit && (
        <div
          data-testid="pws-drawer-bank-audit-panel"
          className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Bank-details audit log ({audit.count})
            </div>
            <button
              type="button"
              onClick={() => setShowAudit(false)}
              className="text-slate-400 hover:text-slate-700 text-xs"
            >
              Close
            </button>
          </div>
          {audit.rows.length === 0 ? (
            <div className="text-xs text-slate-500">No audit events yet.</div>
          ) : (
            <ol className="space-y-1.5 text-xs">
              {audit.rows.map((row) => (
                <li key={row.id} className="text-slate-700">
                  <span className="font-mono text-slate-500">{new Date(row.at).toLocaleString("en-AU")}</span>
                  <span className="mx-1.5 text-slate-400">·</span>
                  <span className="font-semibold text-slate-900">{row.action.replace(/_/g, " ")}</span>
                  <span className="mx-1.5 text-slate-400">·</span>
                  <span>{row.actor_email} ({row.actor_role})</span>
                  {row.changed_fields?.length > 0 && (
                    <span className="ml-2 text-slate-500">
                      [{row.changed_fields.join(", ")}]
                    </span>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

function BankSummaryField({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 font-mono mt-0.5">{value || "—"}</div>
    </div>
  );
}

const EMPTY_BANK_FORM = {
  account_name: "",
  bsb: "",
  account_number: "",
  payid: "",
  remittance_email: "",
  notes: "",
};

function BankFormField({ label, field, form, setForm, fieldError, placeholder, maxLength, testid }) {
  const err = fieldError?.field === field ? fieldError.message : null;
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
        {label}
      </label>
      <input
        type="text"
        value={form[field]}
        onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
        placeholder={placeholder}
        maxLength={maxLength}
        data-testid={testid}
        className={`mt-1 w-full px-2.5 py-1.5 rounded-lg border text-xs focus:ring-1 outline-none bg-white ${
          err
            ? "border-rose-400 focus:border-rose-500 focus:ring-rose-500"
            : "border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
        }`}
      />
      {err && (
        <div data-testid={`${testid}-error`} className="mt-1 text-[10px] text-rose-700">
          {err}
        </div>
      )}
    </div>
  );
}

function BankDetailsForm({ form, setForm, fieldError, saving, onSave, onCancel }) {
  return (
    <div
      data-testid="pws-drawer-bank-form"
      className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3"
    >
      {fieldError && !["account_name", "bsb", "account_number", "payid", "remittance_email", "notes"].includes(fieldError.field) && (
        <div data-testid="pws-drawer-bank-form-error" className="mb-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">
          {fieldError.message}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <BankFormField
          label="Account name *"
          field="account_name"
          form={form}
          setForm={setForm}
          fieldError={fieldError}
          placeholder="e.g. John Smith"
          maxLength={100}
          testid="pws-drawer-bank-form-account-name"
        />
        <BankFormField
          label="BSB *"
          field="bsb"
          form={form}
          setForm={setForm}
          fieldError={fieldError}
          placeholder="123-456"
          maxLength={10}
          testid="pws-drawer-bank-form-bsb"
        />
        <BankFormField
          label="Account number *"
          field="account_number"
          form={form}
          setForm={setForm}
          fieldError={fieldError}
          placeholder="e.g. 12345678"
          maxLength={20}
          testid="pws-drawer-bank-form-account-number"
        />
        <BankFormField
          label="PayID (optional)"
          field="payid"
          form={form}
          setForm={setForm}
          fieldError={fieldError}
          placeholder="e.g. mobile or email PayID"
          maxLength={100}
          testid="pws-drawer-bank-form-payid"
        />
        <BankFormField
          label="Remittance email (optional)"
          field="remittance_email"
          form={form}
          setForm={setForm}
          fieldError={fieldError}
          placeholder="e.g. accounts@partner.com"
          maxLength={254}
          testid="pws-drawer-bank-form-remittance-email"
        />
        <BankFormField
          label="Notes (optional)"
          field="notes"
          form={form}
          setForm={setForm}
          fieldError={fieldError}
          placeholder="Internal notes"
          maxLength={500}
          testid="pws-drawer-bank-form-notes"
        />
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          data-testid="pws-drawer-bank-form-cancel-btn"
          onClick={onCancel}
          disabled={saving}
          className="text-xs font-semibold text-slate-500 hover:text-slate-800 px-2 py-1.5 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="pws-drawer-bank-form-save-btn"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {saving ? "Saving…" : "Save bank details"}
        </button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Admin — Notification prefs (Feb 2026 · P1)
// Read-only surface of the partner's email notification preferences +
// audit shortcut. Admin CANNOT edit (partner is source of truth).
// ═══════════════════════════════════════════════════════════════════
function AdminNotificationPrefsSection({ partnerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/partners/${encodeURIComponent(partnerId)}/notification-preferences`,
        );
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerId]);

  const loadAudit = async () => {
    try {
      const { data: rows } = await api.get(
        `/admin/partners/${encodeURIComponent(partnerId)}/notification-preferences/audit`,
      );
      setAudit(rows);
      setShowAudit(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  if (loading) {
    return (
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
        Loading notification preferences…
      </div>
    );
  }
  if (error) {
    return (
      <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
        {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div
      data-testid="pws-drawer-notif-panel"
      className="mt-4 rounded-xl border border-slate-200 bg-white p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-900">
          Notification preferences
          <span className="text-[10px] text-slate-400 font-normal">(read-only)</span>
        </div>
        <button
          type="button"
          onClick={loadAudit}
          data-testid="pws-drawer-notif-audit-link"
          className="text-[11px] font-semibold text-indigo-700 hover:underline"
        >
          Audit log →
        </button>
      </div>
      <PrefStaticRow
        type="payout_scheduled"
        label="Payout scheduled emails"
        pref={data.payout_scheduled}
      />
      <PrefStaticRow
        type="payout_paid"
        label="Payout paid emails"
        pref={data.payout_paid}
      />
      <PrefStaticRow
        type="customer_report_published"
        label="Customer report published"
        pref={data.customer_report_published}
      />

      {showAudit && audit && (
        <div
          data-testid="pws-drawer-notif-audit-panel"
          className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
              Audit log ({audit.count})
            </div>
            <button
              type="button"
              onClick={() => setShowAudit(false)}
              className="text-slate-500 hover:text-slate-800"
            >
              ×
            </button>
          </div>
          {audit.count === 0 ? (
            <div className="text-[11px] text-slate-500">No changes recorded.</div>
          ) : (
            <ol className="space-y-1.5 text-[11px]">
              {audit.rows.slice(0, 15).map((r, i) => (
                <li key={i} className="border-b border-slate-100 pb-1 last:border-b-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-slate-600">
                      {new Date(r.at).toLocaleString("en-AU")}
                    </span>
                    <span className="font-semibold">{r.action}</span>
                    {r.notification_type && (
                      <span className="text-slate-500">({r.notification_type})</span>
                    )}
                  </div>
                  <div className="text-slate-500 pl-1">
                    {r.previous !== undefined && r.previous !== null && (
                      <>
                        {String(r.previous)} → <span className="font-semibold">{String(r.next)}</span>{" "}
                      </>
                    )}
                    via <span className="italic">{r.source}</span>
                    {r.actor_email && <> by {r.actor_email}</>}
                    {r.detail && <> · {r.detail}</>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}


function PrefStaticRow({ type, label, pref }) {
  const on = !!pref?.email;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
      <div className="min-w-0">
        <div className="text-xs font-semibold text-slate-900">{label}</div>
        <div
          data-testid={`pws-drawer-notif-detail-${type}`}
          className="text-[10.5px] text-slate-500 mt-0.5"
        >
          {pref?.updated_at ? (
            <>
              Last changed {new Date(pref.updated_at).toLocaleString("en-AU")}
              {pref.updated_via && <> · via <span className="italic">{pref.updated_via}</span></>}
            </>
          ) : "—"}
        </div>
      </div>
      <span
        data-testid={`pws-drawer-notif-status-${type}`}
        className={[
          "shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border",
          on
            ? "bg-emerald-100 text-emerald-800 border-emerald-300"
            : "bg-slate-100 text-slate-600 border-slate-300",
        ].join(" ")}
      >
        {on ? "Opted in" : "Opted out"}
      </span>
    </div>
  );
}

